/**
 * Banker Recon Summary Tab
 * Tab 1 of Banker Recon - main overview, LO compensation, and actions
 */

import React from 'react';
import { addCapturedFee, availableCapturedFeeAmount, capturedFeeOwner, type ReconFeeAccountingSnapshot } from '@/utils/banker-recon/feePaymentLedger';
import {
  User,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building2,
  Save,
  CheckCircle2,
  Plus,
  X,
  Check,
  Loader2,
  ChevronDown,
  AlertCircle,
  Eye,
  Scale,
  Minus
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/utils/supabase';
import type { EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';
import {
  type KeyDates,
  type PricingState,
  type CompensationState,
  type CompensationDeductionType,
  type RevenueState,
  type CostsState,
  type UploadedDocument,
  type ValidationIssue,
  DEDUCTION_OPTIONS,
  formatCurrency as formatCurrencyFn,
  formatNumberWithCommas as formatNumberWithCommasFn,
  parseFormattedNumber as parseFormattedNumberFn
} from './types';
import type { BankedReconLineItem, BankedReconResult } from '@/utils/banker-recon/bankedNetProfit';
import { getActiveOverrides, type DraftOverrideEntry } from '@/types/banker-recon';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import type { User as UserType } from '@/types/user';
import {
  getPricingAdjustments,
  getDisplayCategory,
  type LoanPricingAdjustment
} from '@/services/loanPricingService';
import { getServicingLoanByEncompassId } from '@/services/servicingService';
import type { PurchaseAdjustment } from './InvestorAdviceTab';
import {
  calculateCdCreditNettedToWarehouseAdvance,
  calculateExpectedBalance,
  calculateInvestorWireAudit,
  isGovernmentFundingPassThrough
} from '@/utils/banker-recon/wireReconciliation';
import {
  buildManualAdjustment,
  MANUAL_ADJUSTMENT_KIND_LABELS,
  type ManualAdjustmentKind,
  type ManualReconAdjustment
} from '@/utils/banker-recon/manualAdjustments';
import {
  getBankerReconLeFeeAtClosingTotal,
  getBankerReconLeFeeAmount,
  getBankerReconLeFeeBreakdown
} from '@/utils/banker-recon/leFeeAmounts';
import {
  applyLoCompReductionToCompensation,
  LO_COMP_REDUCTION_TYPE,
  type LoCompReductionImportMetadata
} from './loCompReduction';
import {
  isAllowedProfitabilityRoutingTarget,
  isRoutableCreditType,
  PROFITABILITY_ROUTING_TARGET_LABELS,
  type ProfitabilityRouting,
  type ProfitabilityRoutingTarget,
  type ReconLoProfitabilityResult,
  type RoutableCreditType
} from '@/utils/banker-recon/loProfitability';
import {
  APPLICATION_FEE_LINE_TYPE,
  autoCaptureLendwiseRevenueLineType,
  AUTO_CAPTURE_CANONICAL_LINE_TYPES,
  canonicalizeLeFee,
  findConventionalMiPremiumFee,
  isLendwiseScalarOwnedFee,
  isMiPremiumLineType,
  isPaidToLendwise,
  MORTGAGE_INSURANCE_PREMIUM_LINE_TYPE,
  setMiPremiumWithheld,
  type RawEncompassFee
} from '@/utils/banker-recon/leFeeCanonical';
import { isBuydownPassThrough } from '@/utils/banker-recon/buydownPassThrough';
import type { FhaUfmipBridge } from '@/utils/banker-recon/fhaUfmip';
import type { VolumeBonusReconPreview } from '@/services/loVolumeBonusService';

// ============================================================================
// PROPS
// ============================================================================

interface BankerReconSummaryProps {
  feeAccounting?: ReconFeeAccountingSnapshot | null;
  volumeBonusPreview?: VolumeBonusReconPreview | null;
  volumeBonusLoading?: boolean;
  volumeBonusError?: string;
  onRefreshVolumeBonus?: () => void;
  servicerSelectionRequired?: boolean;
  selectedLoan: EncompassLoan | null;
  status: 'draft' | 'finalized' | 'pushed';

  // Shared financial model — single source of truth
  reconResult: BankedReconResult;
  lineItems: BankedReconLineItem[];
  fhaUfmipBridge: FhaUfmipBridge;

  // Key dates (read-only, from warehouse tab)
  keyDates: KeyDates;

  // Pricing (read-only, from investor tab)
  pricing: PricingState;

  // Compensation (editable)
  compensation: CompensationState;
  setCompensation: React.Dispatch<React.SetStateAction<CompensationState>>;
  loCompReductionMetadata: LoCompReductionImportMetadata | null;

  // Costs (for detailed breakdown in summary cards)
  costs: CostsState;

  // Revenue (for loan-specific items like processing/UW fees)
  revenue: RevenueState;
  setRevenue: React.Dispatch<React.SetStateAction<RevenueState>>;

  // Manual adjustments (company income/expense entered by the reviewer)
  manualAdjustments: ManualReconAdjustment[];
  setManualAdjustments: React.Dispatch<React.SetStateAction<ManualReconAdjustment[]>>;

  // Calculated values
  totalWarehouseCosts: number;
  totalInvestorCosts: number;
  totalLoanRevenue: number;
  totalInvestorRevenue: number;
  loPayout: number;
  companyRevenue: number;
  gosAmount: number;
  actualPurchaseDollar: number;
  netProfit: number;
  servicingInterestIncome: number;
  servicingPrincipalCollected: number;

  // Actions
  isSaving: boolean;
  isFinalizing: boolean;
  saveSuccess: boolean;
  onSaveDraft: () => void;
  onFinalize: () => void;
  onUnfinalize: () => void;

  // Dirty state callback
  setIsDirty: (dirty: boolean) => void;

  // Purchase price adjustments from Investor tab
  purchaseAdjustments: PurchaseAdjustment[];

  // Uploaded documents from other tabs
  uploadedDocuments: UploadedDocument[];

  // Wire reconciliation
  wireFromInvestor: number;
  setWireFromInvestor: (value: number) => void;
  wireFromWarehouse: number;
  setWireFromWarehouse: (value: number) => void;
  totalReimbursable: number;
  lenderCreditTimingBasis: 'field4794' | 'total';
  retainedClosingFeeBasis: 'legacy' | 'expandedLeFees';

  // LO selection
  loanOfficers: UserType[];
  loadingOfficers: boolean;
  selectedLOId: string;
  onLOChange: (officerId: string) => void;

  // Override fields (editable in draft, read-only when finalized)
  overrides: DraftOverrideEntry[];
  totalOverrideAmount: number;
  onOverridesChange: (overrides: DraftOverrideEntry[]) => void;
  overrideRecipientOptions: UserType[]; // All active users (LOs + LOAs + admins)

  // Validation issues (drives finalize guard)
  validationIssues: ValidationIssue[];

  // LO Profitability (routing-aware, signed, report-only). Optional overlay —
  // the section only renders when `loProfitability` is provided.
  loProfitability?: ReconLoProfitabilityResult;
  // Fail-closed notice: concession read failed, so profitability is withheld
  // (loProfitability is absent) and saves are blocked until a read succeeds.
  concessionsUnavailable?: boolean;
  profitabilityRouting?: ProfitabilityRouting;
  onProfitabilityRoutingChange?: (type: RoutableCreditType, target: ProfitabilityRoutingTarget) => void;
  availableProfitability?: number | null;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({ value, onChange, className, placeholder, ariaLabel }) => {
  const [displayValue, setDisplayValue] = React.useState(value ? formatNumberWithCommasFn(value) : '');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDisplayValue(value ? formatNumberWithCommasFn(value) : '');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = rawValue.replace(/[^0-9.,-]/g, '');
    setDisplayValue(sanitized);
    onChange(parseFormattedNumberFn(sanitized));
  };

  const handleBlur = () => {
    if (displayValue) {
      const num = parseFormattedNumberFn(displayValue);
      setDisplayValue(num ? formatNumberWithCommasFn(num) : '');
    }
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
};

function sanitizeOverrideBpsInput(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const [whole, ...decimalParts] = cleaned.split('.');
  return decimalParts.length > 0 ? `${whole}.${decimalParts.join('')}` : whole;
}

const parseOverrideBpsInput = (value: string): number | null => {
  const trimmed = sanitizeOverrideBpsInput(value).trim();
  if (!trimmed) {
    return null;
  }

  const raw = Number.parseFloat(trimmed.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(raw)) {
    return null;
  }

  const clamped = Math.max(0, Math.min(60, raw));
  return Math.round(clamped * 10000) / 10000;
};

const formatOverrideBpsInput = (value: number | null): string =>
  value === null || !Number.isFinite(value) ? '' : String(value);

const MANUAL_REVENUE_OPTIONS: Array<{ id: string; description: string }> = [];

// Manual adjustment amounts: income adds to profit, an expense subtracts, a pass-through has no P&L sign.
const MANUAL_AMOUNT_STYLE: Record<ManualAdjustmentKind, { sign: string; className: string }> = {
  income: { sign: '+', className: 'text-green-700' },
  expense: { sign: '−', className: 'text-red-700' },
  pass_through: { sign: '', className: 'text-gray-700' }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) {
    return '—';
  }
  const date = parseAsLocalDate(dateStr);
  if (isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const calculateDaysBetween = (start: string, end: string): number => {
  if (!start || !end) {
    return 0;
  }
  const startDate = parseAsLocalDate(start);
  const endDate = parseAsLocalDate(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 0;
  }
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

// ============================================================================
// COMPONENT
// ============================================================================

export const BankerReconSummary: React.FC<BankerReconSummaryProps> = ({
  feeAccounting,
  selectedLoan,
  status,
  volumeBonusPreview,
  volumeBonusLoading = false,
  volumeBonusError = '',
  onRefreshVolumeBonus,
  reconResult,
  lineItems,
  fhaUfmipBridge,
  keyDates,
  pricing,
  compensation,
  setCompensation,
  loCompReductionMetadata,
  costs,
  revenue,
  setRevenue,
  manualAdjustments,
  setManualAdjustments,
  totalWarehouseCosts,
  totalInvestorCosts,
  totalLoanRevenue,
  totalInvestorRevenue,
  loPayout,
  companyRevenue,
  gosAmount,
  actualPurchaseDollar,
  netProfit,
  servicingInterestIncome,
  servicingPrincipalCollected,
  isSaving,
  isFinalizing,
  saveSuccess,
  onSaveDraft,
  onFinalize,
  servicerSelectionRequired = false,
  onUnfinalize,
  setIsDirty,
  purchaseAdjustments,
  uploadedDocuments,
  wireFromInvestor,
  setWireFromInvestor,
  wireFromWarehouse,
  setWireFromWarehouse,
  lenderCreditTimingBasis,
  retainedClosingFeeBasis,
  loanOfficers,
  loadingOfficers,
  selectedLOId,
  onLOChange,
  overrides,
  totalOverrideAmount,
  onOverridesChange,
  overrideRecipientOptions,
  validationIssues,
  loProfitability,
  concessionsUnavailable = false,
  profitabilityRouting = {},
  onProfitabilityRoutingChange,
  availableProfitability = null
}) => {
  const { toast } = useToast();

  // State for reopen confirmation
  const [showReopenConfirm, setShowReopenConfirm] = React.useState(false);

  // State for Section A fee dropdown
  const [selectedSectionAFee, setSelectedSectionAFee] = React.useState<string>('');

  // Authoritative raw_data.fees[] for the selected loan — used to resolve a generic "Line NNN"
  // le_fee to its real fee (e.g. Line 813 → "Trust Certification Fee") when the description
  // field is null. Fetched lazily (RLS-scoped) so it does not bloat the loan-search payload.
  const [rawLoanFees, setRawLoanFees] = React.useState<RawEncompassFee[] | null>(null);
  React.useEffect(() => {
    const loanId = selectedLoan?.encompass_loan_id;
    if (!loanId) {
      setRawLoanFees(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('encompass_loans')
        .select('fees:raw_data->fees')
        .eq('encompass_loan_id', loanId)
        .eq('deleted_from_encompass', false)
        .maybeSingle();
      if (cancelled || error) {
        return;
      }
      const fees = (data as { fees?: RawEncompassFee[] } | null)?.fees;
      setRawLoanFees(Array.isArray(fees) ? fees : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLoan?.encompass_loan_id]);

  // State for manual revenue dropdown
  const [selectedManualRevenue, setSelectedManualRevenue] = React.useState<string>('');

  // State for collapsible breakdown section
  const [breakdownExpanded, setBreakdownExpanded] = React.useState(true);

  // Manual adjustment entry row (draft only)
  const manualFieldId = React.useId();
  const [manualDescription, setManualDescription] = React.useState('');
  const [manualAmount, setManualAmount] = React.useState(0);
  const [manualKind, setManualKind] = React.useState<ManualAdjustmentKind>('income');
  const manualDraftInput = { description: manualDescription, amount: manualAmount, kind: manualKind };
  const canAddManualAdjustment = buildManualAdjustment(manualDraftInput, 'draft') !== null;

  const addManualAdjustment = () => {
    const entry = buildManualAdjustment(manualDraftInput);
    if (!entry) {
      return;
    }
    setManualAdjustments(prev => [...prev, entry]);
    setManualDescription('');
    setManualAmount(0);
    setManualKind('income');
    setIsDirty(true);
  };

  const removeManualAdjustment = (id: string) => {
    setManualAdjustments(prev => prev.filter(entry => entry.id !== id));
    setIsDirty(true);
  };

  // State for pricing adjustments (for margin category names from NetRevenueCard)
  const [pricingAdjustments, setPricingAdjustments] = React.useState<LoanPricingAdjustment[]>([]);

  // State for servicing link (when finalized and servicing record exists)
  const [hasServicingRecord, setHasServicingRecord] = React.useState(false);

  // Draft string state so decimal typing can preserve intermediate values like "17.".
  const [overrideBpsInputs, setOverrideBpsInputs] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    setOverrideBpsInputs((current) => {
      const next: Record<string, string> = {};

      for (const override of overrides) {
        if (!override.recipientId) {
          next[override.id] = '';
          continue;
        }

        const existing = current[override.id];
        if (existing !== undefined) {
          const parsedExisting = parseOverrideBpsInput(existing);
          const parsedMatches =
            (parsedExisting === null && override.bps === null)
            || (
              parsedExisting !== null
              && override.bps !== null
              && Math.abs(parsedExisting - override.bps) <= 0.00001
            );
          if (parsedMatches) {
            next[override.id] = existing;
            continue;
          }
        }

        next[override.id] = formatOverrideBpsInput(override.bps);
      }

      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(next);
      const isSame = currentKeys.length === nextKeys.length
        && nextKeys.every((key) => current[key] === next[key]);
      return isSame ? current : next;
    });
  }, [overrides]);

  // Load pricing adjustments when loan changes (to get custom category names like "Lendwise Margin")
  React.useEffect(() => {
    if (selectedLoan?.encompass_loan_id) {
      getPricingAdjustments(selectedLoan.encompass_loan_id)
        .then(setPricingAdjustments)
        .catch(() => setPricingAdjustments([]));
    } else {
      setPricingAdjustments([]);
    }
  }, [selectedLoan?.encompass_loan_id]);

  // Check for existing servicing record when finalized
  React.useEffect(() => {
    if (status === 'finalized' && selectedLoan?.encompass_loan_id && selectedLoan?.organization_id) {
      getServicingLoanByEncompassId(selectedLoan.organization_id, selectedLoan.encompass_loan_id)
        .then(record => setHasServicingRecord(!!record))
        .catch(() => setHasServicingRecord(false));
    } else {
      setHasServicingRecord(false);
    }
  }, [status, selectedLoan?.encompass_loan_id, selectedLoan?.organization_id]);

  // Calculate pricing breakdown for variance
  const pricingBreakdown = React.useMemo(() => {
    const adjustments = selectedLoan?.rate_lock_buy_side_adjustments || [];
    const llpaAdjustments = adjustments.filter(adj => adj.priceAdjustmentType === 'BasePrice');
    const marginAdjustments = adjustments.filter(adj => adj.priceAdjustmentType === 'ProfitMargin');

    const totalLLPA = llpaAdjustments.reduce((sum, adj) => sum + adj.rate, 0);
    // Margin is stored as negative in Encompass, we show absolute value
    const totalMargin = marginAdjustments.reduce((sum, adj) => sum + Math.abs(adj.rate), 0);

    const encompassBuyPrice = selectedLoan?.rate_lock_total_buy_price || 0;
    const basePrice = encompassBuyPrice - totalLLPA;
    const investorLockPrice = encompassBuyPrice + totalMargin;

    return { llpaAdjustments, marginAdjustments, totalLLPA, totalMargin, encompassBuyPrice, basePrice, investorLockPrice };
  }, [selectedLoan?.rate_lock_buy_side_adjustments, selectedLoan?.rate_lock_total_buy_price]);

  // Calculate variance (Investor Lock Price vs Actual Purchase Price)
  const actualPrice = pricing.actualPricePercent;
  const variance = actualPrice - pricingBreakdown.investorLockPrice;
  const varianceIsPositive = variance >= 0;

  // Interest netting calculations
  const interestIncome = revenue.loan.find(r => r.id === 'interest_charged')?.amount || 0;
  const warehouseInterestExpense = costs.warehouse.find(c => c.id === 'warehouse_interest')?.amount || 0;
  // Get processing/UW fees
  const processingFee = revenue.loan.find(r => r.id === 'processing_fee')?.amount || 0;
  const underwritingFee = revenue.loan.find(r => r.id === 'underwriting_fee')?.amount || 0;

  // Initial Escrow Deposit from Encompass (NEWHUD.X1719) - money held from borrower
  const escrowDeposit = selectedLoan?.fees_initial_escrow_deposit || 0;

  // Escrow pass-through (collected at closing, deducted by investor — wash for P&L)
  const escrowPassThrough = costs.investor
    .filter(c => c.id.startsWith('escrow_deposit'))
    .reduce((sum, c) => sum + c.amount, 0);
  const isGovernmentFundingCost = (item: { id: string; description: string }) =>
    isGovernmentFundingPassThrough({
      category: 'investor_cost',
      line_type: item.id,
      description: item.description
    }, selectedLoan?.trans_details_loan_type || null);
  const isBuydownCost = (item: { id: string; description: string }) =>
    isBuydownPassThrough({
      category: 'investor_cost',
      line_type: item.id,
      description: item.description
    });

  // totalReimbursable is now passed as a prop from BankerRecon.tsx

  // ============================================================================
  // FINANCIAL VALUES — all from shared reconResult (single source of truth)
  // ============================================================================
  const {
    loPayoutBase: loPayoutBeforeDeductions,
    loPayoutWithReimbursables,
    nonReimbursableUserFees,
    reimbursableItems: reconReimbursableItems,
    nonReimbursableUserFeeItems: reconNonReimbursableUserFeeItems,
    shortageFunded,
    governmentFundingPassThrough,
    buydownPassThrough,
    totalRevenueForDisplay,
    totalCostsForDisplay
  } = reconResult;
  const volumeBonusAmount = reconResult.volumeBonusAmount ?? 0;
  const totalLoCompensation = reconResult.totalLoCompensation ?? loPayoutWithReimbursables;
  const displayTotalRevenueForDisplay = status !== 'draft' ? companyRevenue : totalRevenueForDisplay;
  const displayTotalCostsForDisplay = status !== 'draft' ? companyRevenue - netProfit : totalCostsForDisplay;
  const cureAppliedLenderCredit = revenue.cureAppliedLenderCredit || 0;

  const getDeductionSourceAmount = (type: CompensationDeductionType): number => {
    switch (type) {
      case 'lender_credit':
        return revenue.lenderCredit;
      case 'cure_applied_lender_credit':
        return cureAppliedLenderCredit;
      case 'lo_comp_reduction':
        return loCompReductionMetadata?.reductionAmount ?? 0;
    }
  };

  // Map reconResult items for display compatibility
  const reimbursableItems = reconReimbursableItems.map(r => ({
    id: r.lineType,
    description: r.description,
    amount: r.reimbursementAmount ?? r.amount
  }));
  const nonReimbursableUserFeesList = reconNonReimbursableUserFeeItems.map(f => ({ id: f.lineType, description: f.description, amount: f.amount }));

  // Basis points display helper: converts $ to % of loan amount (4 decimal places)
  const toBpsDisplay = (amount: number) => {
    const loanAmount = selectedLoan?.trans_details_loan_amt || 0;
    if (loanAmount <= 0) {
      return null;
    }
    return ((Math.abs(amount) / loanAmount) * 100).toFixed(4);
  };

  const signedLoEffect = (amount: number) => ({
    prefix: amount < 0 ? '+' : '-',
    className: amount < 0 ? 'text-green-600' : 'text-red-600'
  });

  // Get Section A & B fees from le_fees, excluding already-added fees
  const availableLeFees = React.useMemo(() => {
    if (!selectedLoan?.le_fees) {
      return [];
    }

    // Already-added rows, by canonical line_type (stable identity — not fee name).
    const existingLineTypes = new Set(revenue.loan.map(item => item.id));

    // Section A & B fees that are NOT auto-captured (those net to the warehouse automatically)
    // and NOT already added; show a fee with a borrower OR seller amount.
    return selectedLoan.le_fees
      .filter(fee => fee.section === 'A' || fee.section === 'B')
      .filter(fee => !autoCaptureLendwiseRevenueLineType(fee))
      // Lendwise Processing/UW are owned by the explicit scalars — never selectable (would double-count).
      .filter(fee => !isLendwiseScalarOwnedFee(fee))
      .filter(fee => !existingLineTypes.has(canonicalizeLeFee(fee, rawLoanFees).lineType))
      .filter(fee => {
        const breakdown = getBankerReconLeFeeBreakdown(fee);
        const hasKnownBorrowerCharge = breakdown.basis === 'at_closing' && (
          breakdown.grossAmount > 0 || Number(breakdown.paidOutsideClosingAmount) > 0
        );
        return breakdown.amountAtClosing > 0 || hasKnownBorrowerCharge || Number(fee.sellerAmount) > 0;
      });
  }, [selectedLoan?.le_fees, revenue.loan, rawLoanFees]);

  // ============================================================================
  // REVENUE UPDATE HANDLERS
  // ============================================================================

  const updateLoanRevenue = (id: string, amount: number) => {
    setRevenue(prev => ({
      ...prev,
      loan: prev.loan.map(item =>
        item.id === id ? { ...item, amount } : item
      )
    }));
    setIsDirty(true);
  };

  const updateLoanRevenueReimbursable = (id: string, reimbursable: boolean) => {
    setRevenue(prev => ({
      ...prev,
      loan: prev.loan.map(item =>
        item.id === id ? { ...item, reimbursable } : item
      )
    }));
    setIsDirty(true);
  };

  const updateCreditTiming = (
    field: 'lenderCreditNetsToWarehouseAdvance' | 'cureAppliedLenderCreditNetsToWarehouseAdvance',
    value: boolean
  ) => {
    setRevenue(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
  };

  const renderCreditTimingControl = (
    field: 'lenderCreditNetsToWarehouseAdvance' | 'cureAppliedLenderCreditNetsToWarehouseAdvance',
    value: boolean | null
  ) => {
    const currentValue = value === null ? 'review' : value ? 'nets' : 'post';

    if (status !== 'draft') {
      return (
        <Badge variant="outline" className={cn(
          "text-[10px] px-1 py-0",
          value === null ? "border-amber-300 bg-amber-50 text-amber-700" : "text-gray-600"
        )}>
          {value === null ? 'Review required' : value ? 'Nets to WH advance' : 'Post-advance'}
        </Badge>
      );
    }

    return (
      <Select
        value={currentValue}
        onValueChange={(next) => {
          if (next === 'nets' || next === 'post') {
            updateCreditTiming(field, next === 'nets');
          }
        }}
      >
        <SelectTrigger className={cn(
          "h-7 w-[160px] text-xs",
          value === null && "border-amber-300 bg-amber-50 text-amber-700"
        )}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {value === null && (
            <SelectItem value="review" disabled>Review required</SelectItem>
          )}
          <SelectItem value="post">Post-advance</SelectItem>
          <SelectItem value="nets">Nets to WH advance</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  // ============================================================================
  // CONVENTIONAL MI PREMIUM — "WAREHOUSE WITHHELD MI"
  // ============================================================================

  // One plain checkbox. Checked = the warehouse withheld the Closing Disclosure's MI premium for
  // Lendwise to pay the insurer: the premium row exists as REVENUE that nets to the warehouse advance
  // (expected warehouse proceeds +premium, company revenue +premium) and the parent derives the
  // matching company EXPENSE line (company expenses +premium). Net profit unchanged. Unchecked = no
  // row and no expense. No review gate; the team tracks the actual payment to the insurer.
  const miPremium = React.useMemo(() => {
    const eligible = findConventionalMiPremiumFee(selectedLoan?.le_fees, selectedLoan?.trans_details_loan_type);
    const row = revenue.loan.find(item => isMiPremiumLineType(item.id)) ?? null;
    if (!eligible && !row) {
      return null;
    }
    return {
      eligible,
      withheld: row !== null,
      amount: eligible?.amount ?? row?.amount ?? 0,
      payee: eligible?.payee ?? 'insurer'
    };
  }, [selectedLoan?.le_fees, selectedLoan?.trans_details_loan_type, revenue.loan]);

  const setMiPremiumWithheldChecked = (withheld: boolean) => {
    const amount = miPremium?.amount ?? 0;
    setRevenue(prev => ({ ...prev, loan: setMiPremiumWithheld(prev.loan, withheld, amount) }));
    setIsDirty(true);
  };

  const addSectionAFee = () => {
    if (!selectedSectionAFee) {
      return;
    }
    if (feeAccounting) {
      const fee = feeAccounting.capture.fees.find(candidate => candidate.sourceFeeKey === selectedSectionAFee);
      if (fee) {
        setRevenue(prev => ({ ...prev, loan: addCapturedFee(prev.loan, fee, feeAccounting.capture).rows }));
        setSelectedSectionAFee('');
        setIsDirty(true);
      }
      return;
    }

    // Find by canonical line_type (the Select's value) — stable identity, never fee.name.
    const fee = availableLeFees.find(
      f => canonicalizeLeFee(f, rawLoanFees).lineType === selectedSectionAFee
    );
    if (!fee) {
      return;
    }

    // Canonical key + authoritative name (resolves a generic "Line NNN" via raw_data.fees[]).
    const { lineType, displayName } = canonicalizeLeFee(fee, rawLoanFees);
    const feeAmountAtClosing = getBankerReconLeFeeAtClosingTotal(fee);

    // A fully POC borrower charge stays visible for audit context but must not
    // create a zero-value warehouse row.
    if (feeAmountAtClosing <= 0) {
      return;
    }

    // A fee paid to Lendwise is REVENUE immediately. A third-party fee starts as retained warehouse
    // basis; the parent draft state normalizer converts non-reimbursable retained rows into P&L revenue.
    const row = isPaidToLendwise(fee)
      ? {
          id: lineType,
          description: displayName,
          amount: feeAmountAtClosing,
          warehouse_retained_amount: 0,
          nets_to_warehouse_advance: true
        }
      : {
          id: lineType,
          description: displayName,
          amount: 0,
          warehouse_retained_amount: feeAmountAtClosing,
          nets_to_warehouse_advance: true
        };

    setRevenue(prev => ({
      ...prev,
      loan: [...prev.loan, row]
    }));

    setSelectedSectionAFee('');
    setIsDirty(true);
  };

  const removeLoanRevenue = (id: string) => {
    setRevenue(prev => ({
      ...prev,
      loan: prev.loan.filter(item => item.id !== id)
    }));
    setIsDirty(true);
  };

  const availableManualRevenue = React.useMemo(() => {
    const existingIds = new Set(revenue.loan.map(item => item.id.replace(/_\d+$/, '')));
    return MANUAL_REVENUE_OPTIONS.filter(opt => !existingIds.has(opt.id));
  }, [revenue.loan]);

  const addManualRevenue = () => {
    if (!selectedManualRevenue) {
      return;
    }
    const option = MANUAL_REVENUE_OPTIONS.find(o => o.id === selectedManualRevenue);
    if (!option) {
      return;
    }

    // Auto-populate from le_fees if available
    let amount = 0;
    if (selectedLoan?.le_fees) {
      const matchingFee = (selectedLoan.le_fees as Array<{ name: string; borrowerAmount?: number; totalAmount?: number }>)
        .find(f => f.name === option.description || f.name?.toLowerCase().includes('buydown'));
      if (matchingFee) {
        amount = getBankerReconLeFeeAmount(matchingFee);
      }
    }

    setRevenue(prev => ({
      ...prev,
      loan: [...prev.loan, { id: `${option.id}_${Date.now()}`, description: option.description, amount }]
    }));
    setSelectedManualRevenue('');
    setIsDirty(true);
  };

  // ============================================================================
  // DOCUMENT VIEW HANDLER
  // ============================================================================

  const handleViewDocument = async (storagePath: string) => {
    const { data } = await supabase.storage
      .from('loan-documents')
      .createSignedUrl(storagePath, 3600);

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    } else {
      toast({
        variant: 'destructive',
        title: 'Could not open document',
        description: 'Failed to generate signed URL'
      });
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {status !== 'draft' && (
        <div className={cn(
          "p-4 rounded-lg border-2 flex items-center justify-between",
          status === 'finalized' ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"
        )}>
          <div className="flex items-center gap-3">
            <CheckCircle2 className={cn(
              "h-6 w-6",
              status === 'finalized' ? "text-amber-600" : "text-green-600"
            )} />
            <div>
              <p className={cn(
                "font-semibold",
                status === 'finalized' ? "text-amber-700" : "text-green-700"
              )}>
                {status === 'finalized' ? 'Reconciliation Finalized' : 'Pushed to Encompass'}
              </p>
              <p className="text-sm text-gray-600">
                {status === 'finalized'
                  ? 'This reconciliation has been finalized and payouts have been created.'
                  : 'This reconciliation has been synced with Encompass.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={status === 'finalized' ? 'outline' : 'default'} className={cn(
              status === 'pushed' && "bg-green-600"
            )}>
              {status === 'finalized' ? 'Finalized' : 'Synced'}
            </Badge>
            {status === 'finalized' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReopenConfirm(true)}
                disabled={isFinalizing}
              >
                Reopen
              </Button>
            )}
            {hasServicingRecord && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-blue-600 border-blue-300 hover:bg-blue-50"
                onClick={() => {
                  // Navigate to servicing section via URL param
                  const url = new URL(window.location.href);
                  url.searchParams.set('section', 'servicing');
                  window.location.href = url.toString();
                }}
              >
                <TrendingUp className="h-3.5 w-3.5" />
                View in Servicing
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Reopen Confirmation Dialog */}
      <AlertDialog open={showReopenConfirm} onOpenChange={setShowReopenConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen Reconciliation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revert the status to draft and remove ordinary payout records for recreation. Any allocated volume bonus remains owed and attached to this loan, including its recorded payments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowReopenConfirm(false);
              onUnfinalize();
            }}>
              Reopen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Loan Info Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5 text-gray-500" />
            Loan Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Borrower</Label>
              <p className="font-medium">
                {selectedLoan?.borrower_first_name} {selectedLoan?.borrower_last_name}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Loan Number</Label>
              <p className="font-medium font-mono">{selectedLoan?.trans_details_loan_number || '—'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Loan Amount</Label>
              <p className="font-medium">
                {selectedLoan?.trans_details_loan_amt
                  ? formatCurrencyFn(selectedLoan.trans_details_loan_amt)
                  : '—'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Loan Officer</Label>
              <Select
                value={selectedLOId}
                onValueChange={onLOChange}
                disabled={loadingOfficers || loanOfficers.length === 0 || status !== 'draft' || volumeBonusPreview?.allocated}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue
                    placeholder={
                      loadingOfficers
                        ? "Loading..."
                        : loanOfficers.length === 0
                          ? "No loan officers found"
                          : "Select loan officer"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {loanOfficers.map((officer) => (
                    <SelectItem key={officer.id} value={officer.id}>
                      {officer.firstName} {officer.lastName}
                    </SelectItem>
                  ))}
                  {/* Show saved LO even if not in active officers list */}
                  {selectedLOId && !loanOfficers.find(o => o.id === selectedLOId) && (
                    <SelectItem value={selectedLOId}>
                      {selectedLOId} (inactive)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {!selectedLOId && selectedLoan?.loan_officer_first_name && (
                <p className="text-xs text-amber-600 mt-1">
                  Encompass: {selectedLoan.loan_officer_first_name} {selectedLoan.loan_officer_last_name} (not matched)
                </p>
              )}
            </div>
          </div>

          {/* Document Status Row */}
          <div className="flex items-center gap-6 mt-4 pt-4 border-t">
            {/* Warehouse Reconciliation */}
            {(() => {
              const warehouseDoc = uploadedDocuments.find(d => d.documentType === 'warehouse_reconciliation');
              const hasDoc = !!warehouseDoc;
              const isComplete = warehouseDoc?.processingStatus === 'complete';
              const canView = hasDoc && isComplete && warehouseDoc?.storagePath;

              return (
                <div className="flex items-center gap-2">
                  {hasDoc ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  )}
                  <span className="text-sm text-gray-600">Warehouse Recon:</span>
                  <span className={cn(
                    "text-sm font-medium",
                    hasDoc ? "text-green-600" : "text-amber-600"
                  )}>
                    {hasDoc ? 'Uploaded' : 'Not uploaded'}
                  </span>
                  {canView && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 ml-1"
                      onClick={() => handleViewDocument(warehouseDoc.storagePath)}
                      title="View document"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })()}

            {/* Purchase Advice */}
            {(() => {
              const purchaseDoc = uploadedDocuments.find(d => d.documentType === 'purchase_advice');
              const hasDoc = !!purchaseDoc;
              const isComplete = purchaseDoc?.processingStatus === 'complete';
              const canView = hasDoc && isComplete && purchaseDoc?.storagePath;

              return (
                <div className="flex items-center gap-2">
                  {hasDoc ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  )}
                  <span className="text-sm text-gray-600">Purchase Advice:</span>
                  <span className={cn(
                    "text-sm font-medium",
                    hasDoc ? "text-green-600" : "text-amber-600"
                  )}>
                    {hasDoc ? 'Uploaded' : 'Not uploaded'}
                  </span>
                  {canView && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 ml-1"
                      onClick={() => handleViewDocument(purchaseDoc.storagePath)}
                      title="View document"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Key Dates Summary (Read-only) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-gray-500" />
            Key Dates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Closing Date</Label>
              <p className="font-medium">{formatDate(keyDates.closingDate)}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Funds to Escrow</Label>
              <p className="font-medium">{formatDate(keyDates.fundsToEscrow)}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Funds Received</Label>
              <p className="font-medium">{formatDate(keyDates.fundsReceived)}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Days on Warehouse</Label>
              <p className="font-medium text-amber-600">
                {keyDates.fundsToEscrow && keyDates.fundsReceived
                  ? `${calculateDaysBetween(keyDates.fundsToEscrow, keyDates.fundsReceived)} days`
                  : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Summary (Read-only) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-gray-500" />
            Pricing Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {/* Encompass Price Card */}
            <div className="bg-slate-50 rounded-lg p-4 border space-y-3">
              <p className="text-xs text-gray-500 font-medium">Encompass Price</p>

              {/* Base Price */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Base Price</span>
                <span className="font-mono text-sm">
                  {pricingBreakdown.basePrice ? pricingBreakdown.basePrice.toFixed(4) : '—'}
                </span>
              </div>

              {/* LLPA Adjustments */}
              {pricingBreakdown.llpaAdjustments.length > 0 && (
                <div className="border-t pt-2 space-y-1">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Price Adjustments (LLPA)</p>
                  {pricingBreakdown.llpaAdjustments.map((adj, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span className="text-gray-500 truncate pr-2">
                        {getDisplayCategory(adj.description, pricingAdjustments)}
                      </span>
                      <span className={cn(
                        "font-mono flex-shrink-0",
                        adj.rate > 0 ? 'text-green-600' : adj.rate < 0 ? 'text-red-600' : 'text-gray-600'
                      )}>
                        {adj.rate > 0 ? '+' : ''}{adj.rate.toFixed(4)}
                      </span>
                    </div>
                  ))}
                  {pricingBreakdown.llpaAdjustments.length > 1 && (
                    <div className="flex justify-between text-xs font-medium pt-1 border-t">
                      <span className="text-gray-600">Total Adjustments</span>
                      <span className={cn(
                        "font-mono",
                        pricingBreakdown.totalLLPA > 0 ? 'text-green-600' : pricingBreakdown.totalLLPA < 0 ? 'text-red-600' : 'text-gray-600'
                      )}>
                        {pricingBreakdown.totalLLPA > 0 ? '+' : ''}{pricingBreakdown.totalLLPA.toFixed(4)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Total Price Locked */}
              <div className="flex justify-between items-center py-2 bg-gray-100 px-2 rounded font-medium">
                <span className="text-sm text-gray-700">Total Price Locked</span>
                <span className="font-mono text-sm">
                  {pricingBreakdown.encompassBuyPrice ? pricingBreakdown.encompassBuyPrice.toFixed(4) : '—'}
                </span>
              </div>

              {/* Margin */}
              {pricingBreakdown.marginAdjustments.length > 0 && (
                <div className="border-t pt-2 space-y-1">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Margin</p>
                  {pricingBreakdown.marginAdjustments.map((adj, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span className="text-gray-500 truncate pr-2">
                        {getDisplayCategory(adj.description, pricingAdjustments)}
                      </span>
                      <span className="font-mono flex-shrink-0 text-green-600">
                        +{Math.abs(adj.rate).toFixed(4)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Investor Lock Price */}
              <div className="flex justify-between items-center py-2 bg-green-100 px-2 rounded font-semibold">
                <span className="text-sm text-green-800">Investor Lock Price</span>
                <span className="font-mono text-sm text-green-800">
                  {pricingBreakdown.investorLockPrice ? pricingBreakdown.investorLockPrice.toFixed(4) : '—'}
                </span>
              </div>
            </div>

            {/* Actual Purchase Price - with calculation breakdown */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 space-y-3">
              <p className="text-xs text-blue-600 font-medium">Actual Purchase Price</p>

              {/* Starting point: Investor Lock Price */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Investor Lock Price</span>
                <span className="font-mono text-sm">
                  {pricingBreakdown.investorLockPrice ? pricingBreakdown.investorLockPrice.toFixed(4) : '—'}
                </span>
              </div>

              {/* Purchase Adjustments */}
              {purchaseAdjustments.length > 0 && (
                <div className="border-t pt-2 space-y-1">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Purchase Adjustments</p>
                  {purchaseAdjustments.map((adj) => (
                    <div key={adj.id} className="flex justify-between text-xs">
                      <span className="text-gray-500 truncate pr-2">{adj.description}</span>
                      <span className={cn(
                        "font-mono flex-shrink-0",
                        adj.basisPoints > 0 ? 'text-green-600' : adj.basisPoints < 0 ? 'text-red-600' : 'text-gray-600'
                      )}>
                        {adj.basisPoints > 0 ? '+' : ''}{adj.basisPoints.toFixed(4)}
                      </span>
                    </div>
                  ))}
                  {purchaseAdjustments.length > 1 && (
                    <div className="flex justify-between text-xs font-medium pt-1 border-t">
                      <span className="text-gray-600">Total Adjustments</span>
                      <span className={cn(
                        "font-mono",
                        purchaseAdjustments.reduce((sum, adj) => sum + adj.basisPoints, 0) > 0 ? 'text-green-600' :
                        purchaseAdjustments.reduce((sum, adj) => sum + adj.basisPoints, 0) < 0 ? 'text-red-600' : 'text-gray-600'
                      )}>
                        {purchaseAdjustments.reduce((sum, adj) => sum + adj.basisPoints, 0) > 0 ? '+' : ''}
                        {purchaseAdjustments.reduce((sum, adj) => sum + adj.basisPoints, 0).toFixed(4)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Final: Actual Purchase Price */}
              <div className="flex justify-between items-center py-2 bg-blue-100 px-2 rounded font-semibold">
                <span className="text-sm text-blue-800">Actual Purchase Price</span>
                <span className="font-mono text-sm text-blue-800">
                  {pricing.actualPricePercent ? pricing.actualPricePercent.toFixed(4) : '—'}
                </span>
              </div>

              {purchaseAdjustments.length === 0 && (
                <p className="text-[10px] text-gray-400 text-center">
                  No adjustments — equals Investor Lock Price
                </p>
              )}
            </div>

            {/* Variance / Pickup-Loss */}
            <div className={cn(
              "rounded-lg p-4 border space-y-3",
              varianceIsPositive ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
            )}>
              <p className={cn(
                "text-xs font-medium",
                varianceIsPositive ? "text-green-600" : "text-red-600"
              )}>
                {varianceIsPositive ? 'Pickup' : 'Loss'}
              </p>

              {/* Header */}
              <div className="grid grid-cols-3 gap-2 text-[10px] text-gray-500 uppercase tracking-wide border-b pb-1">
                <span></span>
                <span className="text-right">Basis Pts</span>
                <span className="text-right">Amount</span>
              </div>

              {/* Investor Lock Price */}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <span className="text-gray-600">Investor Lock</span>
                <span className="text-right font-mono">
                  {pricingBreakdown.investorLockPrice ? pricingBreakdown.investorLockPrice.toFixed(4) : '—'}
                </span>
                <span className="text-right font-mono text-gray-600">
                  {selectedLoan?.trans_details_loan_amt && pricingBreakdown.investorLockPrice
                    ? formatCurrencyFn(selectedLoan.trans_details_loan_amt * (pricingBreakdown.investorLockPrice - 100) / 100)
                    : '—'}
                </span>
              </div>

              {/* Actual Purchase Price */}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <span className="text-gray-600">Actual Price</span>
                <span className="text-right font-mono">
                  {pricing.actualPricePercent ? pricing.actualPricePercent.toFixed(4) : '—'}
                </span>
                <span className="text-right font-mono text-gray-600">
                  {selectedLoan?.trans_details_loan_amt && pricing.actualPricePercent
                    ? formatCurrencyFn(selectedLoan.trans_details_loan_amt * (pricing.actualPricePercent - 100) / 100)
                    : '—'}
                </span>
              </div>

              {/* Pickup/Loss Row */}
              <div className={cn(
                "grid grid-cols-3 gap-2 text-xs font-semibold pt-2 border-t",
                varianceIsPositive ? "text-green-700" : "text-red-700"
              )}>
                <span>{varianceIsPositive ? 'Pickup' : 'Loss'}</span>
                <span className="text-right font-mono">
                  {pricing.actualPricePercent && pricingBreakdown.investorLockPrice
                    ? `${varianceIsPositive ? '+' : ''}${variance.toFixed(4)}`
                    : '—'}
                </span>
                <span className="text-right font-mono">
                  {selectedLoan?.trans_details_loan_amt && pricing.actualPricePercent && pricingBreakdown.investorLockPrice
                    ? `${varianceIsPositive ? '+' : ''}${formatCurrencyFn(selectedLoan.trans_details_loan_amt * variance / 100)}`
                    : '—'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        {/* Total Revenue */}
        <Card className="border-2 border-green-200 bg-green-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold font-mono text-green-700">
              +{formatCurrencyFn(displayTotalRevenueForDisplay)}
            </p>

            {/* Category subtotals */}
            <div className="text-xs text-gray-600 mt-3 space-y-1">
              {actualPurchaseDollar > 0 && (
                <div className="flex justify-between">
                  <span>Investor Price</span>
                  <span className="font-mono flex-shrink-0">{formatCurrencyFn(actualPurchaseDollar)}</span>
                </div>
              )}
              {(processingFee + underwritingFee + interestIncome + servicingInterestIncome + nonReimbursableUserFees) > 0 && (
                <div className="flex justify-between">
                  <span>Loan Fees & Interest</span>
                  <span className="font-mono flex-shrink-0">{formatCurrencyFn(processingFee + underwritingFee + interestIncome + servicingInterestIncome + nonReimbursableUserFees)}</span>
                </div>
              )}
            </div>

          </CardContent>
        </Card>

        {/* Total Costs */}
        <Card className="border-2 border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="h-5 w-5 text-red-500" />
              <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">Total Costs</span>
            </div>
            <p className="text-2xl font-bold font-mono text-red-700">
              -{formatCurrencyFn(displayTotalCostsForDisplay)}
            </p>

            {/* Category subtotals */}
            <div className="text-xs text-gray-600 mt-3 space-y-1">
              {loPayoutWithReimbursables !== 0 && (
                <div className="flex justify-between">
                  <span>{loPayoutWithReimbursables < 0 ? 'LO Compensation Offset' : 'LO Compensation'}</span>
                  <span className={cn('font-mono flex-shrink-0', loPayoutWithReimbursables < 0 ? 'text-green-600' : '')}>
                    {formatCurrencyFn(loPayoutWithReimbursables)}
                  </span>
                </div>
              )}
              {volumeBonusAmount > 0 && <div className="flex justify-between gap-2"><span>Volume Bonus</span><span className="font-mono shrink-0">{formatCurrencyFn(volumeBonusAmount)}</span></div>}
              {cureAppliedLenderCredit > 0 && (
                <div className="flex justify-between">
                  <span>Cure Applied to Lender Credit</span>
                  <span className="font-mono flex-shrink-0">{formatCurrencyFn(cureAppliedLenderCredit)}</span>
                </div>
              )}
              {(totalWarehouseCosts - shortageFunded) > 0 && (
                <div className="flex justify-between">
                  <span>Warehouse Costs</span>
                  <span className="font-mono flex-shrink-0">{formatCurrencyFn(totalWarehouseCosts - shortageFunded)}</span>
                </div>
              )}
              {costs.flexcloseCredit > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>FlexClose Credit</span>
                  <span className="font-mono flex-shrink-0">({formatCurrencyFn(costs.flexcloseCredit)})</span>
                </div>
              )}
              {(totalInvestorCosts - escrowPassThrough - governmentFundingPassThrough - buydownPassThrough) !== 0 && (
                <div className="flex justify-between">
                  <span>Investor Costs</span>
                  <span className="font-mono flex-shrink-0">{formatCurrencyFn(totalInvestorCosts - escrowPassThrough - governmentFundingPassThrough - buydownPassThrough)}</span>
                </div>
              )}
              {revenue.lenderCredit > 0 && (
                <div>
                  <div className="flex justify-between gap-2">
                    <span>Lender Credit Given</span>
                    <span className="font-mono flex-shrink-0">{formatCurrencyFn(revenue.lenderCredit)}</span>
                  </div>
                  {feeAccounting && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      General credit {formatCurrencyFn(feeAccounting.capture.credits.nonSpecific)} + lender-paid costs {formatCurrencyFn(feeAccounting.capture.credits.lenderPaid)}. Included once in expenses.
                    </p>
                  )}
                </div>
              )}
              {totalOverrideAmount > 0 && (
                <div className="flex justify-between text-orange-700">
                  <span>Override ({getActiveOverrides(overrides).length} recipient{getActiveOverrides(overrides).length !== 1 ? 's' : ''})</span>
                  <span className="font-mono flex-shrink-0">{formatCurrencyFn(totalOverrideAmount)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-cyan-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              <DollarSign className="h-5 w-5 text-cyan-600" />
              <span className="text-sm font-medium text-cyan-700 uppercase tracking-wide">Net Profit</span>
            </div>

            {/* Side-by-side display - centered with basis points on right */}
            <div className="flex items-baseline justify-between">
              <p className="text-3xl font-bold font-mono text-cyan-700">
                {formatCurrencyFn(netProfit)}
              </p>
              {selectedLoan?.trans_details_loan_amt && selectedLoan.trans_details_loan_amt > 0 && (
                <p className="text-xl font-semibold font-mono text-cyan-600">
                  {((netProfit / selectedLoan.trans_details_loan_amt) * 100).toFixed(3)} pts
                </p>
              )}
            </div>

            <p className="text-xs text-cyan-500 mt-3 text-center">
              Total Revenue − Total Costs
            </p>
          </CardContent>
        </Card>
      </div>

      {/* LO Compensation Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5 text-gray-500" />
            LO Compensation
            {selectedLOId && (() => {
              const lo = loanOfficers.find(o => o.id === selectedLOId);
              return lo ? (
                <span className="text-sm font-normal text-gray-500">
                  — {lo.firstName} {lo.lastName}
                </span>
              ) : null;
            })()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Compensation Inputs */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <Label htmlFor="compType" className="text-xs text-gray-500">Compensation Type</Label>
              <Select
                value={compensation.type}
                onValueChange={(value: 'flat_fee' | 'percentage') => {
                  setCompensation(prev => ({ ...prev, type: value }));
                  setIsDirty(true);
                }}
                disabled={status !== 'draft'}
              >
                <SelectTrigger id="compType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat_fee">Flat Fee</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="compValue" className="text-xs text-gray-500">
                {compensation.type === 'flat_fee' ? 'Amount ($)' : 'Percentage (%)'}
              </Label>
              {compensation.type === 'flat_fee' ? (
                <CurrencyInput
                  value={compensation.value}
                  onChange={(val) => {
                    setCompensation(prev => ({ ...prev, value: val }));
                    setIsDirty(true);
                  }}
                  placeholder="0.00"
                />
              ) : (
                <Input
                  id="compValue"
                  type="number"
                  step="0.001"
                  value={compensation.value || ''}
                  onChange={(e) => {
                    setCompensation(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }));
                    setIsDirty(true);
                  }}
                  placeholder="0"
                  disabled={status !== 'draft'}
                />
              )}
            </div>
          </div>

          {/* Payout Breakdown — Ledger */}
          {(volumeBonusAmount > 0 || volumeBonusLoading || volumeBonusError) && <div className="mb-4 space-y-2 rounded-md border bg-muted/30 p-3" data-testid="recon-volume-bonus">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="recon-volume-bonus">Volume Bonus{volumeBonusPreview?.installment_number ? ` · ${volumeBonusPreview.installment_number} of ${volumeBonusPreview.loan_count}` : ''}</Label>
                <p className="text-xs text-muted-foreground">{volumeBonusPreview?.allocated ? 'Already allocated; this obligation stays attached when reopened.' : 'Assigned at eligible finalization, not paid yet. No second LO Profitability deduction.'}</p>
              </div>
              <Input id="recon-volume-bonus" className="w-40 max-w-full text-right tabular-nums" readOnly
                value={volumeBonusLoading ? 'Checking…' : volumeBonusError ? 'Unavailable' : formatCurrencyFn(volumeBonusAmount)} />
            </div>
            {volumeBonusError && <p role="alert" className="text-sm text-destructive">{volumeBonusError} <Button size="sm" variant="outline" onClick={onRefreshVolumeBonus}>Refresh volume bonus</Button></p>}
          </div>}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payout Breakdown</p>
              <div className="flex gap-6 text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                <span className="w-28 text-right">Amount</span>
                <span className="w-16 text-right">%</span>
              </div>
            </div>

            <div className="space-y-1.5">
              {/* Base Compensation */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700">Base Compensation</span>
                <div className="flex gap-6">
                  <span className="w-28 text-right font-mono">{formatCurrencyFn(loPayoutBeforeDeductions)}</span>
                  <span className="w-16 text-right font-mono text-gray-400">{toBpsDisplay(loPayoutBeforeDeductions) ?? '—'}</span>
                </div>
              </div>

              {/* Deduction rows — named */}
              {compensation.deductions.map((deduction, idx) => (
                <div key={deduction.type} className="flex items-center justify-between text-sm text-red-600">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex-shrink-0">−</span>
                    <span className="truncate">{deduction.label}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">(of {formatCurrencyFn(deduction.sourceAmount)})</span>
                    {status === 'draft' && !deduction.readOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 text-red-400 hover:text-red-600 flex-shrink-0"
                        onClick={() => {
                          setCompensation(prev => ({
                            ...prev,
                            deductions: prev.deductions.filter((_, i) => i !== idx)
                          }));
                          setIsDirty(true);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-6 items-center">
                    {status === 'draft' && !deduction.readOnly ? (
                      <CurrencyInput
                        value={deduction.deductionAmount}
                        onChange={(val) => {
                          const clamped = Math.min(val, deduction.sourceAmount);
                          setCompensation(prev => ({
                            ...prev,
                            deductions: prev.deductions.map((d, i) =>
                              i === idx ? { ...d, deductionAmount: Math.max(0, clamped) } : d
                            )
                          }));
                          setIsDirty(true);
                        }}
                        className="w-28 text-right text-red-600"
                      />
                    ) : (
                      <span className="w-28 text-right font-mono">−{formatCurrencyFn(deduction.deductionAmount)}</span>
                    )}
                    <span className="w-16 text-right font-mono text-red-400">{toBpsDisplay(deduction.deductionAmount) ?? '—'}</span>
                  </div>
                </div>
              ))}

              {/* Reimbursable rows — named, sign/color from amount */}
              {reimbursableItems.map(item => (
                <div key={item.id} className={cn("flex items-center justify-between text-sm", item.amount >= 0 ? "text-green-600" : "text-red-600")}>
                  <span>{item.amount >= 0 ? '+' : '−'} {item.description} <span className="text-[10px] text-gray-400">(reimb.)</span></span>
                  <div className="flex gap-6">
                    <span className="w-28 text-right font-mono">
                      {item.amount >= 0 ? '' : '−'}{formatCurrencyFn(Math.abs(item.amount))}
                    </span>
                    <span className={cn("w-16 text-right font-mono", item.amount >= 0 ? "text-green-400" : "text-red-400")}>
                      {toBpsDisplay(item.amount) ?? '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {volumeBonusAmount > 0 && <div className="mt-2 flex flex-wrap justify-between gap-2 text-sm"><span>+ Volume Bonus{volumeBonusPreview?.installment_number ? ` · ${volumeBonusPreview.installment_number} of ${volumeBonusPreview.loan_count}` : ''}</span><span className="font-mono">{formatCurrencyFn(volumeBonusAmount)}</span></div>}
            {/* Separator + Total */}
            <div className="border-t border-gray-300 mt-3 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">Total LO Payout</span>
                <div className="flex gap-6">
                  <span className={`w-28 text-right font-mono font-bold text-base ${totalLoCompensation >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrencyFn(totalLoCompensation)}</span>
                  <span className="w-16 text-right font-mono font-semibold text-gray-500">{toBpsDisplay(totalLoCompensation) ?? '—'}</span>
                </div>
              </div>
            </div>

            {/* Add Deduction — only in draft, only unused types */}
            {status === 'draft' && (() => {
              const addedTypes = new Set(compensation.deductions.map(d => d.type));
              const availableOptions = DEDUCTION_OPTIONS.filter(opt =>
                opt.type !== 'lender_credit'
                && !addedTypes.has(opt.type)
                && getDeductionSourceAmount(opt.type) > 0
              );
              if (availableOptions.length === 0) {
                return null;
              }
              return (
                <div className="mt-3">
                  <Select
                    value=""
                    onValueChange={(value) => {
                      const option = DEDUCTION_OPTIONS.find(o => o.type === value);
                      if (!option) {
                        return;
                      }
                      const sourceAmount = getDeductionSourceAmount(option.type);
                      if (option.type === LO_COMP_REDUCTION_TYPE) {
                        setCompensation(prev =>
                          applyLoCompReductionToCompensation(prev, loCompReductionMetadata, 'product_pricing') ?? prev
                        );
                        setIsDirty(true);
                        return;
                      }
                      setCompensation(prev => ({
                        ...prev,
                        deductions: [...(prev.deductions ?? []), {
                          type: option.type,
                          label: option.label,
                          sourceAmount,
                          deductionAmount: sourceAmount
                        }]
                      }));
                      setIsDirty(true);
                    }}
                  >
                    <SelectTrigger className="h-7 w-44 text-xs border-dashed">
                      <div className="flex items-center gap-1">
                        <Minus className="h-3 w-3" />
                        <span>Add Deduction</span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {availableOptions.map(opt => (
                        <SelectItem key={opt.type} value={opt.type}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}

            {/* Fail-closed: concession read failed → profitability withheld, saves blocked */}
            {concessionsUnavailable && !loProfitability && (
              <div className="border-t border-gray-300 mt-3 pt-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">LO Profitability</p>
                <p className="mt-1.5 text-sm text-destructive">
                  Concessions unavailable — profitability is hidden and saving is blocked until company concessions can be read. Retry in a moment.
                </p>
              </div>
            )}

            {/* LO Profitability — routing-aware, signed, report-only (optional overlay) */}
            {loProfitability && (
            <div className="border-t border-gray-300 mt-3 pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">LO Profitability</p>
                <div className="flex gap-6 text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                  <span className="w-28 text-right">Amount</span>
                  <span className="w-16 text-right">%</span>
                </div>
              </div>

              <div className="space-y-1.5">
                {/* Gross — toggle-independent pricing gain */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Gross</span>
                  <div className="flex gap-6">
                    <span className="w-28 text-right font-mono">{formatCurrencyFn(loProfitability.gross)}</span>
                    <span className="w-16 text-right font-mono text-gray-400">{toBpsDisplay(loProfitability.gross) ?? '—'}</span>
                  </div>
                </div>

                {/* One row per routable credit, with one authoritative destination control. */}
                {loProfitability.components.map(component => {
                  // effectAmount is the line's signed contribution to net:
                  // negative = cost eroding profitability (red −$), positive =
                  // add-back e.g. borrower-paid comp (green +$), 0 = allocated/no effect.
                  const effectColor = component.effectAmount === 0
                    ? 'text-gray-400'
                    : component.effectAmount > 0
                      ? 'text-green-600'
                      : 'text-red-600';
                  const effectText = component.effectAmount === 0
                    ? formatCurrencyFn(0)
                    : `${component.effectAmount > 0 ? '+' : '−'}${formatCurrencyFn(Math.abs(component.effectAmount))}`;
                  // Routable credits (lender credit / cure / LOCR) get a
                  // destination control; the non-routable Case-2 lines
                  // (LO Compensation, Borrower-Paid Comp) render read-only.
                  const routableType: RoutableCreditType | null = isRoutableCreditType(component.type)
                    ? component.type
                    : null;
                  // Prefer the routing map's target (source of truth) over the
                  // component's embedded copy so the toggle reflects live routing.
                  const currentTarget = routableType
                    ? (profitabilityRouting[routableType]?.target ?? component.target)
                    : component.target;
                  return (
                    <div key={component.type}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-gray-700">{component.label}</span>
                        {routableType && (status === 'draft' ? (
                          <Select
                            value={currentTarget ?? undefined}
                            onValueChange={(next) => {
                              if (routableType && isAllowedProfitabilityRoutingTarget(routableType, next)) {
                                onProfitabilityRoutingChange?.(routableType, next);
                              }
                            }}
                          >
                            <SelectTrigger
                              aria-label={`${component.label} destination`}
                              className="h-6 w-[150px] text-[11px]"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lo_profitability">
                                {PROFITABILITY_ROUTING_TARGET_LABELS.lo_profitability}
                              </SelectItem>
                              <SelectItem value="base_comp">
                                {PROFITABILITY_ROUTING_TARGET_LABELS.base_comp}
                              </SelectItem>
                              {routableType === 'lender_credit' && (
                                <SelectItem value="company">
                                  {PROFITABILITY_ROUTING_TARGET_LABELS.company}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 text-gray-600">
                            {currentTarget
                              ? PROFITABILITY_ROUTING_TARGET_LABELS[currentTarget]
                              : '—'}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-6">
                        <span className={cn('w-28 text-right font-mono', effectColor)}>
                          {effectText}
                        </span>
                        <span className="w-16 text-right font-mono text-gray-400">{toBpsDisplay(component.effectAmount) ?? '—'}</span>
                      </div>
                    </div>
                    {/* Audit sub-note: entered dollars vs waterfall-derived pricing impact */}
                    {component.type === 'company_concession' && (
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        {formatCurrencyFn(component.sourceAmount)} entered · {component.points.toFixed(3)} pts · {formatCurrencyFn(component.amount)} pricing impact
                      </p>
                    )}
                    {component.type === 'lo_compensation_variance' && component.varianceBasis && (
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        Plan {component.varianceBasis.planPoints.toFixed(3)} pts ({formatCurrencyFn(component.varianceBasis.planAmount)}) · Built into pricing {component.varianceBasis.builtInPoints.toFixed(3)} pts ({formatCurrencyFn(component.varianceBasis.builtInAmount)})
                      </p>
                    )}
                    </div>
                  );
                })}

                {/* Net LO Profitability — signed (green ≥ 0, red < 0) */}
                <div className="border-t border-gray-200 mt-2 pt-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-800">Net LO Profitability</span>
                  <div className="flex gap-6">
                    <span className={cn('w-28 text-right font-mono font-bold text-base', loProfitability.net >= 0 ? 'text-green-700' : 'text-red-700')}>
                      {formatCurrencyFn(loProfitability.net)}
                    </span>
                    <span className="w-16 text-right font-mono font-semibold text-gray-500">{toBpsDisplay(loProfitability.net) ?? '—'}</span>
                  </div>
                </div>

                {/* Available profitability (informational) */}
                <p className="text-[11px] text-gray-400 pt-1">
                  Available profitability:{' '}
                  {availableProfitability === null ? 'Unavailable' : formatCurrencyFn(availableProfitability)}
                </p>
              </div>
            </div>
            )}
          </div>

          {/* Override Section — multiple overrides */}
          {selectedLOId && (
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200 mb-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-orange-700 font-semibold uppercase tracking-wide">Overrides</Label>
                {status === 'draft' && overrides.length < 3 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOverridesChange([...overrides, { id: crypto.randomUUID(), recipientId: null, recipientName: null, bps: null }])}
                    className="h-6 text-xs text-orange-700 hover:text-orange-900"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Override
                  </Button>
                )}
              </div>
              {overrides.length === 0 && (
                <p className="text-xs text-orange-400 italic">No overrides configured</p>
              )}
              {overrides.map((override) => {
                const loanAmt = selectedLoan?.trans_details_loan_amt || 0;
                const payout = override.bps && override.bps > 0 && loanAmt > 0
                  ? loanAmt * override.bps / 10000
                  : 0;
                return (
                  <div key={override.id} className="grid grid-cols-[1fr_100px_120px_32px] gap-3 items-end mb-2">
                    {/* Recipient */}
                    <div>
                      {status !== 'draft' ? (
                        <p className="text-sm font-medium">{override.recipientName ?? override.recipientId ?? 'None'}</p>
                      ) : (
                        <Select
                          value={override.recipientId ?? 'none'}
                          onValueChange={(value) => {
                            const newId = value === 'none' ? null : value;
                            const officer = newId ? overrideRecipientOptions.find(o => o.id === newId) : null;
                            const updated = overrides.map(o =>
                              o.id === override.id
                                ? { ...o, recipientId: newId, recipientName: officer ? `${officer.firstName} ${officer.lastName}` : null, bps: newId ? o.bps : null }
                                : o
                            );
                            onOverridesChange(updated);
                          }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select recipient" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {overrideRecipientOptions.map((person) => (
                              <SelectItem key={person.id} value={person.id}>
                                {person.firstName} {person.lastName}
                              </SelectItem>
                            ))}
                            {override.recipientId && !overrideRecipientOptions.find(o => o.id === override.recipientId) && (
                              <SelectItem value={override.recipientId}>
                                {override.recipientName || override.recipientId} (inactive)
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    {/* BPS */}
                    <div>
                      {status !== 'draft' ? (
                        <p className="text-sm font-medium">{override.bps ?? '—'} bps</p>
                      ) : (
                        <Input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9]*[.]?[0-9]*"
                          placeholder="0.0"
                          disabled={!override.recipientId}
                          value={overrideBpsInputs[override.id] ?? formatOverrideBpsInput(override.bps)}
                          onChange={(e) => {
                            const draft = sanitizeOverrideBpsInput(e.currentTarget.value);
                            setOverrideBpsInputs((current) => ({ ...current, [override.id]: draft }));
                            const parsed = parseOverrideBpsInput(draft);
                            const updated = overrides.map(o =>
                              o.id === override.id
                                ? { ...o, bps: parsed }
                                : o
                            );
                            onOverridesChange(updated);
                          }}
                          onBlur={(e) => {
                            const parsed = parseOverrideBpsInput(e.currentTarget.value);
                            setOverrideBpsInputs((current) => ({
                              ...current,
                              [override.id]: formatOverrideBpsInput(parsed)
                            }));
                          }}
                          aria-label={`${override.recipientName || 'Override'} BPS`}
                          className="h-9"
                        />
                      )}
                    </div>
                    {/* Calculated payout */}
                    <p className="text-sm font-bold text-orange-600 text-right">
                      {payout > 0 ? formatCurrencyFn(payout) : '—'}
                    </p>
                    {/* Remove button (draft only) */}
                    {status === 'draft' ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-orange-400 hover:text-red-600"
                        onClick={() => onOverridesChange(overrides.filter(o => o.id !== override.id))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : <div />}
                  </div>
                );
              })}
              {/* Total override summary when >1 */}
              {getActiveOverrides(overrides).length > 1 && (
                <div className="border-t border-orange-200 pt-2 mt-2 flex justify-end gap-2">
                  <span className="text-xs text-orange-700">Total Override:</span>
                  <span className="text-sm font-bold text-orange-600">{formatCurrencyFn(totalOverrideAmount)}</span>
                </div>
              )}
            </div>
          )}

          {/* Net Profit Summary with Collapsible Breakdown */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-700 font-medium">Net Profit</p>
              <p className="text-2xl font-bold text-blue-700 font-mono">{formatCurrencyFn(netProfit)}</p>
              {selectedLoan?.trans_details_loan_amt && selectedLoan.trans_details_loan_amt > 0 && (
                <p className="text-sm text-blue-600 font-mono mt-1">
                  {((netProfit / selectedLoan.trans_details_loan_amt) * 100).toFixed(3)} pts
                </p>
              )}

              {/* Collapsible Breakdown */}
              {selectedLoan?.trans_details_loan_amt && selectedLoan.trans_details_loan_amt > 0 && (
                <Collapsible open={breakdownExpanded} onOpenChange={setBreakdownExpanded} className="mt-3">
                  <CollapsibleTrigger className="flex items-center justify-between w-full pt-3 border-t border-blue-200 text-sm text-blue-600 hover:text-blue-800">
                    <span className="font-medium">View Breakdown</span>
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform",
                      breakdownExpanded && "rotate-180"
                    )} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 pt-3">
                    {/* Header */}
                    <div className="grid grid-cols-3 gap-2 text-[10px] text-blue-500 uppercase tracking-wide">
                      <span></span>
                      <span className="text-right">Basis Pts</span>
                      <span className="text-right">Amount</span>
                    </div>
                    {/* Actual Purchase Price */}
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <span className="text-blue-600">Actual Purchase Price</span>
                      <span className="text-right font-mono text-blue-600">
                        {pricing.actualPricePercent ? (pricing.actualPricePercent - 100).toFixed(3) : '—'}
                      </span>
                      <span className="text-right font-mono text-blue-700">{formatCurrencyFn(actualPurchaseDollar)}</span>
                    </div>
                    {/* Less LO Commission — named */}
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <span className="text-blue-600">
                        {loPayout < 0 ? 'LO Compensation Deduction Offset' : 'Less LO Commission'}{(() => {
                          const lo = loanOfficers.find(o => o.id === selectedLOId);
                          return lo ? ` — ${lo.firstName} ${lo.lastName}` : '';
                        })()}
                      </span>
                      <span className={cn('text-right font-mono', signedLoEffect(loPayout).className)}>
                        {signedLoEffect(loPayout).prefix}{(Math.abs(loPayout) / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                      </span>
                      <span className={cn('text-right font-mono', signedLoEffect(loPayout).className)}>
                        {signedLoEffect(loPayout).prefix}{formatCurrencyFn(Math.abs(loPayout))}
                      </span>
                    </div>
                    {/* Override lines — right after LO Commission */}
                    {volumeBonusAmount > 0 && <div className="grid grid-cols-3 gap-2 text-sm"><span>Less Volume Bonus</span><span className="text-right font-mono text-destructive">−{toBpsDisplay(volumeBonusAmount)}</span><span className="text-right font-mono text-destructive">−{formatCurrencyFn(volumeBonusAmount)}</span></div>}
                    {getActiveOverrides(overrides).map(ovr => {
                      const amt = selectedLoan.trans_details_loan_amt! * ovr.bps / 10000;
                      if (amt <= 0) {
                        return null;
                      }
                      return (
                        <div key={ovr.id} className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-orange-700">
                            Override to {ovr.recipientName || 'Recipient'} ({ovr.bps} bps)
                          </span>
                          <span className="text-right font-mono text-red-600">
                            -{(amt / selectedLoan.trans_details_loan_amt! * 100).toFixed(3)}
                          </span>
                          <span className="text-right font-mono text-red-600">-{formatCurrencyFn(amt)}</span>
                        </div>
                      );
                    })}
                    {/* Cure applied to lender credit — borrower-facing cost, optionally offset by LO deduction */}
                    {cureAppliedLenderCredit > 0 && (
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <span className="text-blue-600 flex flex-wrap items-center gap-2">
                          <span>Less Cure Applied to Lender Credit</span>
                          {renderCreditTimingControl('cureAppliedLenderCreditNetsToWarehouseAdvance', revenue.cureAppliedLenderCreditNetsToWarehouseAdvance)}
                        </span>
                        <span className="text-right font-mono text-red-600">
                          -{(cureAppliedLenderCredit / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                        </span>
                        <span className="text-right font-mono text-red-600">-{formatCurrencyFn(cureAppliedLenderCredit)}</span>
                      </div>
                    )}
                    {/* GOS */}
                    <div className="grid grid-cols-3 gap-2 text-sm border-t border-blue-200 pt-2">
                      <span className="text-blue-700 font-medium">GOS (Gain on Sale)</span>
                      <span className="text-right font-mono text-blue-700 font-medium">
                        {(gosAmount / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                      </span>
                      <span className="text-right font-mono text-blue-700 font-medium">{formatCurrencyFn(gosAmount)}</span>
                    </div>
                    {/* Processing Fee */}
                    {processingFee > 0 && (
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <span className="text-blue-600">Processing Fee</span>
                        <span className="text-right font-mono text-green-600">
                          +{(processingFee / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                        </span>
                        <span className="text-right font-mono text-green-600">+{formatCurrencyFn(processingFee)}</span>
                      </div>
                    )}
                    {/* Underwriting Fee */}
                    {underwritingFee > 0 && (
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <span className="text-blue-600">Underwriting Fee</span>
                        <span className="text-right font-mono text-green-600">
                          +{(underwritingFee / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                        </span>
                        <span className="text-right font-mono text-green-600">+{formatCurrencyFn(underwritingFee)}</span>
                      </div>
                    )}
                    {/* Individual non-reimbursable user fees (company revenue) */}
                    {nonReimbursableUserFeesList.map(fee => (
                      <div key={fee.id} className="grid grid-cols-3 gap-2 text-sm">
                        <span className="text-blue-600">{fee.description}</span>
                        <span className="text-right font-mono text-green-600">
                          +{(fee.amount / selectedLoan.trans_details_loan_amt! * 100).toFixed(3)}
                        </span>
                        <span className="text-right font-mono text-green-600">+{formatCurrencyFn(fee.amount)}</span>
                      </div>
                    ))}
                    {/* Company Revenue Total */}
                    <div className="grid grid-cols-3 gap-2 text-sm border-t border-blue-300 pt-2">
                      <span className="text-blue-800 font-semibold">Company Revenue</span>
                      <span className="text-right font-mono text-blue-800 font-semibold">
                        {(companyRevenue / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                      </span>
                      <span className="text-right font-mono text-blue-800 font-semibold">{formatCurrencyFn(companyRevenue)}</span>
                    </div>

                    {/* Interest & Costs Section */}
                    <div className="border-t border-blue-200 pt-2 mt-2">
                      {/* Interest Charged at Closing */}
                      {interestIncome > 0 && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-blue-600">Interest Charged at Closing</span>
                          <span className="text-right font-mono text-green-600">
                            +{(interestIncome / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                          </span>
                          <span className="text-right font-mono text-green-600">+{formatCurrencyFn(interestIncome)}</span>
                        </div>
                      )}
                      {/* Servicing Interest Income (from interim payments) */}
                      {servicingInterestIncome > 0 && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-blue-600">Servicing Interest Income</span>
                          <span className="text-right font-mono text-green-600">
                            +{(servicingInterestIncome / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                          </span>
                          <span className="text-right font-mono text-green-600">+{formatCurrencyFn(servicingInterestIncome)}</span>
                        </div>
                      )}
                      {/* Servicing Principal Collected (informational) */}
                      {servicingPrincipalCollected > 0 && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-gray-400 italic">Principal Collected</span>
                          <span></span>
                          <span className="text-right font-mono text-gray-400">{formatCurrencyFn(servicingPrincipalCollected)}</span>
                        </div>
                      )}
                      {/* Investor Costs/Credits group */}
                      {(costs.investor.some(c => !c.id.startsWith('escrow_deposit') && !isGovernmentFundingCost(c) && !isBuydownCost(c) && c.amount !== 0) || escrowPassThrough > 0 || governmentFundingPassThrough > 0 || buydownPassThrough > 0) && (
                        <>
                          <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                            <span className="text-gray-500 font-semibold uppercase tracking-wide">Investor</span>
                            <span></span>
                            <span></span>
                          </div>
                          {costs.investor
                            .filter(c => !c.id.startsWith('escrow_deposit') && !isGovernmentFundingCost(c) && !isBuydownCost(c) && c.amount !== 0)
                            .map(item => {
                              const isCredit = item.amount < 0;
                              const absAmount = Math.abs(item.amount);
                              return (
                                <div key={item.id} className="grid grid-cols-3 gap-2 text-sm pl-2">
                                  <span className="text-blue-600">
                                    {isCredit ? `${item.description} Credit` : `Less ${item.description}`}
                                  </span>
                                  <span className={`text-right font-mono ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                                    {isCredit ? '+' : '-'}{(absAmount / selectedLoan.trans_details_loan_amt! * 100).toFixed(3)}
                                  </span>
                                  <span className={`text-right font-mono ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                                    {isCredit ? '+' : '-'}{formatCurrencyFn(absAmount)}
                                  </span>
                                </div>
                              );
                            })}
                          {/* Escrow pass-through info (wash - not affecting profit) */}
                          {escrowPassThrough > 0 && (
                            <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                              <span className="text-blue-400 italic text-xs">Escrow Pass-Through (wash)</span>
                              <span></span>
                              <span className="text-right font-mono text-gray-400 text-xs">{formatCurrencyFn(escrowPassThrough)}</span>
                            </div>
                          )}
                          {governmentFundingPassThrough > 0 && (
                            <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                              <span className="text-blue-400 italic text-xs">Government Funding Pass-Through</span>
                              <span></span>
                              <span className="text-right font-mono text-gray-400 text-xs">{formatCurrencyFn(governmentFundingPassThrough)}</span>
                            </div>
                          )}
                          {buydownPassThrough > 0 && (
                            <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                              <span className="text-blue-400 italic text-xs">Temporary Subsidy Buydown Pass-Through</span>
                              <span></span>
                              <span className="text-right font-mono text-gray-400 text-xs">{formatCurrencyFn(buydownPassThrough)}</span>
                            </div>
                          )}
                        </>
                      )}
                      {/* Warehouse Costs/Credits group */}
                      {costs.warehouse.some(c => c.amount !== 0) && (
                        <>
                          <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                            <span className="text-gray-500 font-semibold uppercase tracking-wide">Warehouse</span>
                            <span></span>
                            <span></span>
                          </div>
                          {warehouseInterestExpense > 0 && (
                            <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                              <span className="text-blue-600">Less Warehouse Interest</span>
                              <span className="text-right font-mono text-red-600">
                                -{(warehouseInterestExpense / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                              </span>
                              <span className="text-right font-mono text-red-600">-{formatCurrencyFn(warehouseInterestExpense)}</span>
                            </div>
                          )}
                          {costs.warehouse
                            .filter(c => c.id !== 'warehouse_interest' && c.id !== 'shortage_funded' && c.amount !== 0)
                            .map(item => (
                              <div key={item.id} className="grid grid-cols-3 gap-2 text-sm pl-2">
                                <span className="text-blue-600">Less {item.description}</span>
                                <span className="text-right font-mono text-red-600">
                                  -{(item.amount / selectedLoan.trans_details_loan_amt! * 100).toFixed(3)}
                                </span>
                                <span className="text-right font-mono text-red-600">-{formatCurrencyFn(item.amount)}</span>
                              </div>
                            ))}
                          {/* FlexClose Credit (warehouse lender credit) */}
                          {costs.flexcloseCredit > 0 && (
                            <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                              <span className="text-blue-600">FlexClose Credit</span>
                              <span className="text-right font-mono text-green-600">
                                +{(costs.flexcloseCredit / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                              </span>
                              <span className="text-right font-mono text-green-600">+{formatCurrencyFn(costs.flexcloseCredit)}</span>
                            </div>
                          )}
                        </>
                      )}
                      {/* Lender Credit */}
                      {revenue.lenderCredit > 0 && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-blue-600 flex flex-wrap items-center gap-2">
                            <span>Less Lender Credit Given</span>
                            {renderCreditTimingControl('lenderCreditNetsToWarehouseAdvance', revenue.lenderCreditNetsToWarehouseAdvance)}
                          </span>
                          <span className="text-right font-mono text-red-600">
                            -{(revenue.lenderCredit / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                          </span>
                          <span className="text-right font-mono text-red-600">-{formatCurrencyFn(revenue.lenderCredit)}</span>
                        </div>
                      )}
                      {/* MI premium paid to the insurer — the expense leg of "Warehouse withheld MI"
                          (the revenue leg sits in Loan-Specific Revenue; net profit unchanged) */}
                      {miPremium?.withheld && miPremium.amount > 0 && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-blue-600">Less MI Premium paid to {miPremium.payee}</span>
                          <span className="text-right font-mono text-red-600">
                            -{(miPremium.amount / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                          </span>
                          <span className="text-right font-mono text-red-600">-{formatCurrencyFn(miPremium.amount)}</span>
                        </div>
                      )}
                      {/* Manual expenses (manual income is listed with the other company fees above) */}
                      {manualAdjustments.filter(entry => entry.kind === 'expense').map(entry => (
                        <div key={entry.id} className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-blue-600">Less {entry.description}</span>
                          <span className="text-right font-mono text-red-600">
                            -{(entry.amount / selectedLoan.trans_details_loan_amt! * 100).toFixed(3)}
                          </span>
                          <span className="text-right font-mono text-red-600">-{formatCurrencyFn(entry.amount)}</span>
                        </div>
                      ))}
                      {/* Shortage Funded info line (cash-flow, not P&L) */}
                      {shortageFunded > 0 && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-blue-400 italic text-xs">Shortage Funded (cash-flow, not P&L)</span>
                          <span></span>
                          <span className="text-right font-mono text-gray-400 text-xs">{formatCurrencyFn(shortageFunded)}</span>
                        </div>
                      )}
                      {/* Note: LO Reimbursables are NOT subtracted from Net Profit here because they're
                          already excluded from Company Revenue via nonReimbursableUserFees filter.
                          They're added back in the Expected Balance (Net Profit + LO + Reimbursables). */}
                    </div>

                    {/* Net Profit Total */}
                    <div className="grid grid-cols-3 gap-2 text-sm border-t border-blue-400 pt-2 bg-blue-100 -mx-2 px-2 py-2 rounded">
                      <span className="text-blue-900 font-bold">Net Profit</span>
                      <span className="text-right font-mono text-blue-900 font-bold">
                        {(netProfit / selectedLoan.trans_details_loan_amt * 100).toFixed(3)}
                      </span>
                      <span className="text-right font-mono text-blue-900 font-bold">{formatCurrencyFn(netProfit)}</span>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Loan-Specific Revenue (Encompass fees and captured revenue) */}
      <Card data-testid="banker-recon-loan-revenue">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Loan-Specific Revenue</CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            // Default Encompass fees (always present, read-only)
            const DEFAULT_FEE_IDS = ['processing_fee', 'underwriting_fee', 'interest_charged'];

            // Check if a fee is read-only (default fees OR fees added from LE dropdown)
            const isReadOnly = (id: string) =>
              DEFAULT_FEE_IDS.includes(id) || id.startsWith('section_a_') || id === MORTGAGE_INSURANCE_PREMIUM_LINE_TYPE;

            return (
              <div className="space-y-1">
                {revenue.loan.map(item => {
                  // Warehouse-retained rows have amount 0 + a warehouse_retained_amount. In editable
                  // drafts these are usually reimbursable pass-through rows; non-reimbursable retained
                  // rows are normalized to amount by the parent state boundary.
                  const whOnly = !item.amount && Number(item.warehouse_retained_amount) > 0;
                  // Auto-captured Lendwise rows are authoritative — re-derived
                  // from le_fees at load + re-upserted at save — so they're read-only + non-removable
                  // even though a revenue row (amount > 0) is NOT whOnly.
                  const isAutoCaptured = AUTO_CAPTURE_CANONICAL_LINE_TYPES.has(item.id);
                  const isMiPremiumRow = isMiPremiumLineType(item.id);
                  const rowReadOnly = isReadOnly(item.id) || whOnly || isAutoCaptured || !!item.fee_source;
                  const sourceOwned = item.fee_source?.owner === 'encompass' || item.fee_source?.owner === 'doc_order';
                  return (
                  <div key={item.id} className="flex items-center gap-2">
                    {/* Main row content inside gray area */}
                    <div className={cn('flex-1 bg-gray-50 rounded-lg px-4 py-1.5', (isMiPremiumRow || !!item.fee_source) && 'min-w-0')}>
                      <div className={cn('flex items-center justify-between', (isMiPremiumRow || !!item.fee_source) && 'flex-wrap gap-2')}>
                        <div className={cn('flex items-center gap-2', (isMiPremiumRow || !!item.fee_source) && 'min-w-0 flex-wrap')}>
                          <span className="text-gray-400">├─</span>
                          <span className="text-sm text-gray-600">{item.description}</span>
                          {item.fee_source && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">
                              {item.fee_source.payer === 'lender' ? 'Lender paid' : `${item.fee_source.payer} paid`} · {item.fee_source.timing === 'PAC' ? 'at closing' : 'POC'}
                            </Badge>
                          )}
                          {isReadOnly(item.id) && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">
                              Encompass
                            </Badge>
                          )}
                          {whOnly && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-600 border-blue-300 bg-blue-50">
                              Nets via warehouse
                            </Badge>
                          )}
                          {isAutoCaptured && !whOnly && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-600 border-blue-300 bg-blue-50">
                              {item.id === APPLICATION_FEE_LINE_TYPE
                                ? 'Lendwise application fee · auto-captured'
                                : 'Lendwise discount · auto-captured'}
                            </Badge>
                          )}
                        </div>
                        <div className={cn('flex items-center gap-3', isMiPremiumRow && 'ml-auto shrink-0', !!item.fee_source && 'ml-auto max-w-full flex-wrap justify-end')}>
                          {/* Reimbursable checkbox - independent from warehouse-retained cash treatment.
                              Never offered for the MI premium row: ticking it would route the premium
                              into the LO payout. */}
                          {!DEFAULT_FEE_IDS.includes(item.id) && !isAutoCaptured && !isMiPremiumLineType(item.id) && status === 'draft' && (
                            <div className="flex items-center gap-1.5">
                              <Checkbox
                                id={`reimburse-${item.id}`}
                                checked={item.reimbursable || false}
                                disabled={item.fee_source?.owner === 'doc_order'}
                                onCheckedChange={(checked) => updateLoanRevenueReimbursable(item.id, !!checked)}
                              />
                              <Label htmlFor={`reimburse-${item.id}`} className="text-xs text-gray-500 cursor-pointer">
                                Reimburse to LO
                              </Label>
                            </div>
                          )}
                          {/* Show reimbursable badge for non-draft */}
                          {!DEFAULT_FEE_IDS.includes(item.id) && !isAutoCaptured && !isMiPremiumLineType(item.id) && status !== 'draft' && item.reimbursable && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 text-green-600 border-green-300 bg-green-50">
                              Reimbursable
                            </Badge>
                          )}
                          {rowReadOnly ? (
                            // Read-only display (Encompass fee, or warehouse-retained row showing its retained amount)
                            <span className="w-32 text-right font-mono text-sm text-gray-700">
                              {formatCurrencyFn(whOnly ? Number(item.warehouse_retained_amount) : item.amount)}
                            </span>
                          ) : (
                            // Editable input for other fees
                            <CurrencyInput
                              value={item.amount}
                              onChange={(val) => updateLoanRevenue(item.id, val)}
                              className="w-32 text-right"
                              placeholder="0.00"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Remove button outside the gray area (the MI premium row is owned by its checkbox) */}
                    {!DEFAULT_FEE_IDS.includes(item.id) && !isAutoCaptured && !sourceOwned && !isMiPremiumLineType(item.id) && status === 'draft' ? (
                      <button
                        type="button"
                        onClick={() => removeLoanRevenue(item.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove fee"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : (
                      // Spacer to keep alignment consistent
                      <div className="w-6" />
                    )}
                  </div>
                  );
                })}

                {/* Conventional MI premium — "Warehouse withheld MI" (revenue + expense, net profit unchanged) */}
                {miPremium && (
                  <div className="flex items-center gap-2 pt-3">
                    <div className="flex-1 bg-gray-50 rounded-lg px-4 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {status === 'draft' ? (
                          <Checkbox
                            id="mi-premium-withheld"
                            checked={miPremium.withheld}
                            onCheckedChange={(checked) => setMiPremiumWithheldChecked(!!checked)}
                          />
                        ) : (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 text-gray-600">
                            {miPremium.withheld ? 'Withheld' : 'Not withheld'}
                          </Badge>
                        )}
                        <Label htmlFor="mi-premium-withheld" className="text-sm text-gray-700 cursor-pointer">
                          Warehouse withheld MI — {formatCurrencyFn(miPremium.amount)} ({miPremium.payee})
                        </Label>
                        {!miPremium.eligible && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-300 bg-amber-50 text-amber-700">
                            not on the current Closing Disclosure
                          </Badge>
                        )}
                        <p className="basis-full text-xs text-gray-500">
                          Checked: adds the premium to expected warehouse proceeds, company revenue, and company
                          expenses. Net profit unchanged. The team tracks the payment to {miPremium.payee}.
                        </p>
                      </div>
                    </div>
                    <div className="w-6" />
                  </div>
                )}

                {/* LE Fee Dropdown (Section A & B) - only show when there are fees to add */}
                {status === 'draft' && !feeAccounting && availableLeFees.length > 0 && (
                  <div className="flex items-center gap-2 pt-3">
                    <div className="flex-1 flex items-center gap-2">
                      <Select
                        value={selectedSectionAFee}
                        onValueChange={setSelectedSectionAFee}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Add fee from Loan Estimate..." />
                        </SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-2rem)]">
                          {availableLeFees.map((fee, idx) => {
                            // Value = canonical line_type (stable identity, not fee.name); label =
                            // authoritative name (resolves a generic "Line NNN" via raw_data.fees[]).
                            const { lineType, displayName } = canonicalizeLeFee(fee, rawLoanFees);
                            const breakdown = getBankerReconLeFeeBreakdown(fee);
                            const sellerAmount = Number(fee.sellerAmount) || 0;
                            const isFullyPoc = breakdown.basis === 'at_closing' &&
                              breakdown.amountAtClosing <= 0 && sellerAmount <= 0;
                            const amountLabel = breakdown.basis === 'at_closing'
                              ? `${formatCurrencyFn(breakdown.amountAtClosing)} at closing ` +
                                `(${formatCurrencyFn(breakdown.grossAmount)} total · ` +
                                `${formatCurrencyFn(breakdown.paidOutsideClosingAmount || 0)} POC)`
                              : breakdown.basis === 'legacy_gross'
                                ? `${formatCurrencyFn(breakdown.amountAtClosing)} gross — not re-synced`
                                : formatCurrencyFn(breakdown.amountAtClosing);
                            return (
                            <SelectItem
                              key={`${lineType}-${idx}`}
                              value={lineType}
                              disabled={isFullyPoc}
                              textValue={`${displayName} — ${amountLabel}`}
                            >
                              <span className="flex min-w-0 w-full flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                <span className="truncate font-medium">{displayName}</span>
                                <span className="text-muted-foreground font-mono text-xs sm:whitespace-nowrap sm:text-sm">
                                  {amountLabel}
                                </span>
                              </span>
                            </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addSectionAFee}
                        disabled={!selectedSectionAFee}
                        className="gap-1"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>
                    <div className="w-6" />
                  </div>
                )}

                {status === 'draft' && feeAccounting && (
                  <div className="flex min-w-0 items-center gap-2 pt-3">
                    <Select value={selectedSectionAFee} onValueChange={setSelectedSectionAFee}>
                      <SelectTrigger className="min-w-0 flex-1" aria-label="Add fee from Loan Estimate">
                        <SelectValue placeholder="Add fee from Loan Estimate..." />
                      </SelectTrigger>
                      <SelectContent className="max-w-[calc(100vw-2rem)]">
                        {feeAccounting.capture.fees.filter(fee => !capturedFeeOwner(fee) && Object.values(fee.payments).some(p => p.total > 0)).map(fee => {
                          const available = availableCapturedFeeAmount(revenue.loan, fee);
                          const closing = Object.values(fee.payments).reduce((sum, p) => sum + p.atClosing, 0);
                          const label = available > 0 ? `${formatCurrencyFn(available)} available at closing`
                            : closing > 0 ? 'Already included' : 'Paid outside closing';
                          return (
                            <SelectItem key={fee.sourceFeeKey} value={fee.sourceFeeKey} disabled={available <= 0} textValue={`${fee.name} — ${label}`}>
                              <span className="flex min-w-0 flex-col gap-0.5">
                                <span>{fee.name}</span>
                                <span className="text-xs text-muted-foreground">{label}</span>
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="outline" onClick={addSectionAFee} disabled={!selectedSectionAFee}>
                      <Plus className="mr-1 h-4 w-4" />Add
                    </Button>
                  </div>
                )}

                {/* Manual Revenue Dropdown */}
                {status === 'draft' && availableManualRevenue.length > 0 && (
                  <div className="flex items-center gap-2 pt-2">
                    <div className="flex-1 flex items-center gap-2">
                      <Select
                        value={selectedManualRevenue}
                        onValueChange={setSelectedManualRevenue}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Add manual revenue..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableManualRevenue.map(opt => (
                            <SelectItem key={opt.id} value={opt.id}>
                              {opt.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={addManualRevenue}
                        disabled={!selectedManualRevenue}
                        className="gap-1"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>
                    <div className="w-6" />
                  </div>
                )}

                {/* Subtotal */}
                <div className="flex items-center gap-2 pt-3 mt-2 border-t">
                  <div className="flex-1 flex items-center justify-between py-2 font-semibold">
                    <span className="text-gray-700">Loan Revenue Subtotal</span>
                    <span className="font-mono text-success">
                      {formatCurrencyFn(totalLoanRevenue)}
                    </span>
                  </div>
                  <div className="w-6" />
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {selectedLoan?.trans_details_loan_type?.toUpperCase() === 'FHA' && (
        <Card className={cn(
          'border-2',
          fhaUfmipBridge.finalizationErrors.length > 0
            ? 'border-warning/50 bg-warning/5'
            : 'border-primary/30 bg-primary/5'
        )}>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-primary" />
                FHA Upfront Mortgage Insurance
              </CardTitle>
              <Badge variant={fhaUfmipBridge.isFinalizable ? 'secondary' : 'outline'}>
                {fhaUfmipBridge.flowMode.replace(/_/g, ' ')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm lg:grid-cols-4">
              <div>
                <p className="text-muted-foreground">Premium collected (969)</p>
                <p className="font-mono font-semibold">
                  {formatCurrencyFn(fhaUfmipBridge.premiumAmount || 0)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Financed (1045)</p>
                <p className="font-mono font-semibold">
                  {fhaUfmipBridge.financedAmount === null
                    ? '—'
                    : formatCurrencyFn(fhaUfmipBridge.financedAmount)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Borrower cash portion</p>
                <p className="font-mono font-semibold">
                  {fhaUfmipBridge.financedAmount === null
                    ? '—'
                    : formatCurrencyFn(fhaUfmipBridge.borrowerCashAmount)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Warehouse retained</p>
                <p className="font-mono font-semibold">
                  {formatCurrencyFn(fhaUfmipBridge.warehousePremiumAmount)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">HUD remittance (3081)</p>
                <p className="font-mono font-semibold">
                  {fhaUfmipBridge.remittedAmount === null
                    ? '—'
                    : formatCurrencyFn(fhaUfmipBridge.remittedAmount)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Remitted date (3079)</p>
                <p className="font-medium">{formatDate(fhaUfmipBridge.remittedDate)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Remitted by (3080)</p>
                <p className="truncate font-medium" title={fhaUfmipBridge.remittedBy || undefined}>
                  {fhaUfmipBridge.remittedBy || '—'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Verification (3082)</p>
                <p className="truncate font-mono font-medium" title={fhaUfmipBridge.verificationCode || undefined}>
                  {fhaUfmipBridge.verificationCode || '—'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between border-t pt-3 text-sm">
              <div>
                <p className="font-medium">Company UFMIP variance</p>
                <p className="text-xs text-muted-foreground">
                  HUD remittance less premium collected; LO compensation is unchanged.
                </p>
              </div>
              <span className={cn(
                'font-mono text-base font-semibold',
                fhaUfmipBridge.companyVarianceAmount > 0
                  ? 'text-destructive'
                  : 'text-success'
              )}>
                {formatCurrencyFn(fhaUfmipBridge.companyVarianceAmount)}
              </span>
            </div>

            {fhaUfmipBridge.finalizationErrors.length > 0 && (
              <div
                role="alert"
                className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm"
              >
                <p className="font-medium">Finalization blocked</p>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
                  {fhaUfmipBridge.finalizationErrors.map(error => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  Complete FHA Management in Encompass, wait for the targeted sync or webhook,
                  then Save Draft and retry.
                </p>
              </div>
            )}
            {fhaUfmipBridge.finalizationErrors.length === 0 && (
              <p role="status" className="text-xs text-muted-foreground">
                Canonical UFMIP capture verified. The collected premium clears against the HUD payment.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Manual Adjustments: amounts inside the warehouse wire that the importers don't cover. Income and
          expense move the expected wire and net profit; a pass-through moves only the wire (clearing). */}
      {(status === 'draft' || manualAdjustments.length > 0) && (
        <Card data-testid="banker-recon-manual-adjustments">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Manual Adjustments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {manualAdjustments.length > 0 && (
              <ul className="space-y-1" aria-label="Manual adjustments">
                {manualAdjustments.map(entry => (
                  <li key={entry.id} className="flex items-center gap-2">
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-gray-50 px-4 py-1.5 text-sm">
                      <span className="min-w-0 flex-1 basis-40 break-words text-gray-700">{entry.description}</span>
                      {entry.kind === 'pass_through' && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 text-muted-foreground">
                          {MANUAL_ADJUSTMENT_KIND_LABELS.pass_through}
                        </Badge>
                      )}
                      <span className={cn('font-mono', MANUAL_AMOUNT_STYLE[entry.kind].className)}>
                        {MANUAL_AMOUNT_STYLE[entry.kind].sign}{formatCurrencyFn(entry.amount)}
                      </span>
                    </div>
                    {status === 'draft' ? (
                      <button
                        type="button"
                        onClick={() => removeManualAdjustment(entry.id)}
                        className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                        aria-label={`Remove ${entry.description}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : (
                      <div className="w-6" />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {status === 'draft' && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    aria-label="Adjustment description"
                    placeholder="Description, e.g. Property taxes – Lee County"
                    maxLength={80}
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                    className="min-w-0 flex-1 basis-48"
                  />
                  <CurrencyInput
                    ariaLabel="Adjustment amount"
                    value={manualAmount}
                    onChange={setManualAmount}
                    className="w-32 text-right"
                    placeholder="0.00"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span id={`${manualFieldId}-kind`} className="text-xs text-gray-600">Type</span>
                    <RadioGroup
                      aria-labelledby={`${manualFieldId}-kind`}
                      value={manualKind}
                      onValueChange={(value) => setManualKind(value as ManualAdjustmentKind)}
                      className="flex flex-wrap gap-x-4 gap-y-1"
                    >
                      {(Object.entries(MANUAL_ADJUSTMENT_KIND_LABELS) as Array<[ManualAdjustmentKind, string]>).map(([kind, label]) => (
                        <div key={kind} className="flex items-center gap-1.5">
                          <RadioGroupItem id={`${manualFieldId}-${kind}`} value={kind} />
                          <Label htmlFor={`${manualFieldId}-${kind}`} className="text-sm font-normal cursor-pointer">{label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addManualAdjustment}
                    disabled={!canAddManualAdjustment}
                    className="ml-auto"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add adjustment
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Wire Reconciliation Card */}
      <Card data-testid="banker-recon-wire" className="min-w-0 border-2 border-violet-200 bg-violet-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-violet-700">
            <Scale className="h-5 w-5" />
            Wire Reconciliation
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            // === Investor → Warehouse Audit ===
            const loanAmount = selectedLoan?.trans_details_loan_amt || 0;
            const grossPurchase = loanAmount * (pricing.actualPricePercent / 100);
            const investorAudit = calculateInvestorWireAudit({
              loanAmount,
              actualPurchasePricePercent: pricing.actualPricePercent,
              totalInvestorRevenue,
              totalInvestorCosts,
              servicingPrincipalCollected,
              wireFromInvestor
            });
            const expectedInvestorWire = investorAudit.displayExpectedInvestorWire;
            const investorDifference = investorAudit.displayDifference;
            const isInvestorBalanced = investorAudit.isInvestorBalanced;

            // === Warehouse → Us Audit (cash-flow formula from FirstFunding reconciliation) ===
            const haircutPercent = costs.warehouseHaircutPercent || 0;
            const selectedLoanRecord = selectedLoan as Record<string, unknown> | null;
            const leFields = selectedLoanRecord?.le_fields as Record<string, unknown> | null | undefined;
            const cdCreditNettedToWarehouseAdvance = calculateCdCreditNettedToWarehouseAdvance({
              rawLenderCredit4794: selectedLoanRecord?.trans_details_non_specific_lender_credit,
              lenpcc: leFields?.LENPCC,
              persistedLenderCredit: revenue.lenderCredit,
              persistedLenderCreditIsCurrentPnlBasis: false,
              cureAppliedLenderCredit: revenue.cureAppliedLenderCredit,
              lenderCreditNetsToWarehouseAdvance: revenue.lenderCreditNetsToWarehouseAdvance,
              cureAppliedLenderCreditNetsToWarehouseAdvance: revenue.cureAppliedLenderCreditNetsToWarehouseAdvance,
              lenderCreditTimingBasis
            });
            const whResult = calculateExpectedBalance({
              loanAmount,
              processingFee,
              underwritingFee,
              interestIncome,
              escrowDeposit,
              lineItems,
              loanType: selectedLoan?.trans_details_loan_type || null,
              fhaUfmipWarehousePremium: fhaUfmipBridge.warehousePremiumAmount,
              haircutPercent,
              wireFromInvestor,
              totalWarehouseCosts,
              shortageFunded,
              flexcloseCredit: costs.flexcloseCredit,
              retainedClosingFeeBasis,
              basisMode: 'autoCdCashRequired',
              cdCreditNettedToWarehouseAdvance
            }, wireFromWarehouse);
            const {
              warehouseAdvance,
              warehouseCostsExShortage,
              expectedBalance,
              warehouseDifference,
              isWarehouseBalanced,
              basisModeUsed,
              cashNeededAtClosing
            } = whResult;
            const manualWire = whResult.manualWireAdjustment ?? 0;
            const isCdCashRequiredMode = basisModeUsed === 'cdCashRequired';
            const warehouseCashDeductionLabel = isCdCashRequiredMode
              ? 'Cash Needed at Closing'
              : `Warehouse Advance${haircutPercent > 0 ? ` (${haircutPercent}% haircut)` : ''}`;
            const warehouseCashDeduction = isCdCashRequiredMode
              ? (cashNeededAtClosing ?? 0)
              : warehouseAdvance;
            const warehouseCostsForDisplay = isCdCashRequiredMode
              ? totalWarehouseCosts
              : warehouseCostsExShortage;

            return (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Left column: Investor → Warehouse */}
                <div className="min-w-0 space-y-4">
                  <div className="text-center pb-2 border-b border-violet-200">
                    <span className="text-xs font-medium text-violet-600 uppercase tracking-wide">
                      Investor → Warehouse
                    </span>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Wire from Investor</Label>
                    <p className="text-[10px] text-muted-foreground mb-1">
                      Gross amount investor sent to warehouse
                    </p>
                    <CurrencyInput
                      ariaLabel="Wire from Investor"
                      value={wireFromInvestor}
                      onChange={(val) => {
                        setWireFromInvestor(val);
                        setIsDirty(true);
                      }}
                      className="w-full"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Expected Investor Wire</Label>
                    <p className="text-[10px] text-muted-foreground mb-1">
                      {servicingPrincipalCollected > 0
                        ? 'Loan × Price + Inv. Revenue − Inv. Costs − Principal Collected'
                        : 'Loan × Price + Inv. Revenue − Inv. Costs'}
                    </p>
                    <div className="bg-white rounded-lg border px-3 py-2">
                      <p className="font-mono text-lg font-semibold text-violet-700">
                        {pricing.actualPricePercent > 0 ? formatCurrencyFn(expectedInvestorWire) : '—'}
                      </p>
                      {pricing.actualPricePercent > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
                          <div className="flex flex-wrap justify-between gap-x-2">
                            <span>Gross Purchase:</span>
                            <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(grossPurchase)}</span>
                          </div>
                          {totalInvestorRevenue > 0 && (
                            <div className="flex flex-wrap justify-between gap-x-2 text-success">
                              <span>+ Investor Revenue:</span>
                              <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(totalInvestorRevenue)}</span>
                            </div>
                          )}
                          {totalInvestorCosts > 0 && (
                            <div className="flex flex-wrap justify-between gap-x-2 text-destructive">
                              <span>− Investor Costs:</span>
                              <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(totalInvestorCosts)}</span>
                            </div>
                          )}
                          {servicingPrincipalCollected > 0 && (
                            <div className="flex flex-wrap justify-between gap-x-2 text-warning">
                              <span>− Principal Collected:</span>
                              <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(servicingPrincipalCollected)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Difference</Label>
                    <div className="bg-white rounded-lg border px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className={cn(
                          "font-mono text-lg font-semibold",
                          wireFromInvestor === 0 || pricing.actualPricePercent === 0 ? "text-muted-foreground" :
                          isInvestorBalanced ? "text-success" : "text-destructive"
                        )}>
                          {wireFromInvestor === 0 || pricing.actualPricePercent === 0 ? '—' : formatCurrencyFn(investorDifference)}
                        </p>
                        {wireFromInvestor > 0 && pricing.actualPricePercent > 0 && (
                          <Badge className={cn(
                            "shrink-0",
                            isInvestorBalanced
                              ? "bg-green-100 text-green-700 border-green-300"
                              : "bg-red-100 text-red-700 border-red-300"
                          )}>
                            {isInvestorBalanced ? (
                              <span className="flex items-center gap-1">
                                <Check className="h-3 w-3" />
                                BALANCED
                              </span>
                            ) : (
                              <span>DIFFERENCE</span>
                            )}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column: Warehouse → Us */}
                <div className="min-w-0 space-y-4">
                  <div className="text-center pb-2 border-b border-violet-200">
                    <span className="text-xs font-medium text-violet-600 uppercase tracking-wide">
                      Warehouse → Us
                    </span>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Wire from Warehouse</Label>
                    <p className="text-[10px] text-muted-foreground mb-1">
                      Gross amount warehouse sent to us
                    </p>
                    <CurrencyInput
                      ariaLabel="Wire from Warehouse"
                      value={wireFromWarehouse}
                      onChange={(val) => {
                        setWireFromWarehouse(val);
                        setIsDirty(true);
                      }}
                      className="w-full"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Expected Balance</Label>
                    <p className="text-[10px] text-muted-foreground mb-1">
                      {isCdCashRequiredMode
                        ? 'Inv. Wire − Cash Needed at Closing − WH Costs + FlexClose'
                        : 'Inv. Wire − WH Advance − WH Costs − Shortage + FlexClose'}
                      {manualWire !== 0 && ' ± Manual Adjustments'}
                    </p>
                    <div className="bg-white rounded-lg border px-3 py-2">
                      <p className="font-mono text-lg font-semibold text-violet-700">
                        {wireFromInvestor > 0 ? formatCurrencyFn(expectedBalance) : '—'}
                      </p>
                      {wireFromInvestor > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
                          <div className="flex flex-wrap justify-between gap-x-2">
                            <span>Wire from Investor:</span>
                            <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(wireFromInvestor)}</span>
                          </div>
                          <div className="flex flex-wrap justify-between gap-x-2 text-destructive">
                            <span>− {warehouseCashDeductionLabel}:</span>
                            <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(warehouseCashDeduction)}</span>
                          </div>
                          <div className="flex flex-wrap justify-between gap-x-2 text-destructive">
                            <span>− Warehouse Costs:</span>
                            <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(warehouseCostsForDisplay)}</span>
                          </div>
                          {!isCdCashRequiredMode && shortageFunded > 0 && (
                            <div className="flex flex-wrap justify-between gap-x-2 text-destructive">
                              <span>− Shortage Return:</span>
                              <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(shortageFunded)}</span>
                            </div>
                          )}
                          {costs.flexcloseCredit > 0 && (
                            <div className="flex flex-wrap justify-between gap-x-2 text-success">
                              <span>+ FlexClose Credit:</span>
                              <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(costs.flexcloseCredit)}</span>
                            </div>
                          )}
                          {manualWire !== 0 && (
                            <>
                              <div className="flex flex-wrap justify-between gap-x-2 border-t pt-0.5">
                                <span>Calculated before adjustments:</span>
                                <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(whResult.expectedBalanceBeforeAdjustments)}</span>
                              </div>
                              <div className={cn('flex flex-wrap justify-between gap-x-2', manualWire > 0 ? 'text-success' : 'text-destructive')}>
                                <span>{manualWire > 0 ? '+' : '−'} Manual Adjustments:</span>
                                <span className="ml-auto shrink-0 font-mono">{formatCurrencyFn(Math.abs(manualWire))}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Difference</Label>
                    <div className="bg-white rounded-lg border px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className={cn(
                          "font-mono text-lg font-semibold",
                          wireFromWarehouse === 0 ? "text-muted-foreground" :
                          isWarehouseBalanced ? "text-success" : "text-destructive"
                        )}>
                          {wireFromWarehouse === 0 ? '—' : formatCurrencyFn(warehouseDifference)}
                        </p>
                        {wireFromWarehouse > 0 && (
                          <Badge className={cn(
                            "shrink-0",
                            isWarehouseBalanced
                              ? "bg-green-100 text-green-700 border-green-300"
                              : "bg-red-100 text-red-700 border-red-300"
                          )}>
                            {isWarehouseBalanced ? (
                              <span className="flex items-center gap-1">
                                <Check className="h-3 w-3" />
                                BALANCED
                              </span>
                            ) : (
                              <span>DIFFERENCE</span>
                            )}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="outline"
              className={cn("gap-2", saveSuccess && "text-green-600 border-green-600")}
              onClick={onSaveDraft}
              disabled={isSaving || saveSuccess || isFinalizing || !selectedLoan || status !== 'draft'}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saveSuccess ? (
                <Check className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Draft'}
            </Button>
            <Button
              className="gap-2"
              onClick={onFinalize}
              title={servicerSelectionRequired ? 'Select the servicer on the Servicing tab' : undefined}
              disabled={servicerSelectionRequired || volumeBonusLoading || !!volumeBonusError || isSaving || isFinalizing || !selectedLoan || status !== 'draft' || validationIssues.some(i => i.type === 'error')}
            >
              <CheckCircle2 className={cn("h-4 w-4", isFinalizing && "animate-spin")} />
              {isFinalizing ? 'Finalizing...' : 'Finalize'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
